/**
 * Thin API client — reuses the SAME same-origin, cookie-based Lucia session
 * flow as apps/web's apiFetch (apps/web/src/lib/api.ts). Deliberately
 * minimal for B-1 (scaffold only): screen-level data fetching (storyPct,
 * jobItems, gfScore, etc. — B-3..B-6) lands on top of this in later briefs.
 * Kept as its own module (not copy-pasted per screen) so every future screen
 * calls through one hardened fetch wrapper.
 *
 * B-2 adds `startAgentSession`, the SAME live POST /api/agent-session route
 * apps/web's corpus canvas already calls (requireAuth-gated, same Lucia
 * cookie this module already sends on every request) — the mint used by
 * @careeropt/voice-client's useLivingConversation, bound here so the shared
 * package never imports either app's api.ts directly (see packages/
 * voice-client/src/useLivingConversation.ts's module header).
 *
 * B-3 adds the corpus read/write functions the Refine tab's card canvas needs:
 * getCorpusRoles / getCorpusAchievements (GET /corpus/roles, GET /corpus/
 * achievements), importCorpusText (POST /corpus/import), and rehomeAchievement
 * (PUT /corpus/achievements/:id/rehome). Every shape below is COPIED verbatim
 * from apps/web/src/lib/api.ts (the same backend, the same wire contract — see
 * that file's RoleRecord L78, AchievementRecord L95, ImportResult L126,
 * RehomeRequest L796, RehomeResult L805, rehomeAchievement L825) — nothing here
 * is a new field or a new route. apps/web's own useCorpus.ts (L35-73, L260-268)
 * is the reference implementation for how each of these gets called.
 */

import type { PresencePaste, ResumeDocument, ResumeLinterReport } from '@careeropt/schema';
import type { AgentSessionRequest, AgentSessionResponse } from '@careeropt/voice-client';
export type { PresencePaste, ResumeDocument, ResumeLinterReport } from '@careeropt/schema';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
/** The API's ORIGIN (API_BASE minus its /api suffix) — for the /auth/* routes,
 *  which live beside /api on the worker, not under it. Empty string when
 *  API_BASE is relative — same-origin in BOTH environments now: Vite's dev
 *  proxy locally, the Pages proxy (public/_worker.js) in production (the
 *  R188 session-holding fix; supersedes the R187 absolute-URL shape, which
 *  left the session cookie third-party — set on the worker origin, dropped
 *  from cross-site fetches by Chrome's third-party-cookie policy). */
export const API_ORIGIN = API_BASE.endsWith('/api') ? API_BASE.slice(0, -'/api'.length) : '';

/** Where the OAuth START leg (useAuth's signIn navigation) must go. This one
 *  leg is the DELIBERATE exception to same-origin: Google's registered
 *  redirect_uri lives on the api worker's origin, so the state cookie the
 *  callback verifies must be set there too — starting the flow through the
 *  Pages proxy would land the state cookie on the app origin instead and the
 *  callback would 400 ("Missing state cookie"). A top-level navigation makes
 *  the worker origin first-party for that moment, so its state cookie always
 *  sticks. Dev: falls back to API_ORIGIN ('' behind Vite's proxy —
 *  unchanged; the local worker and its callback share localhost). Production:
 *  the worker origin (VITE_OAUTH_ORIGIN when set, else the same hardcoded
 *  origin the Pages proxy relays to). The SESSION cookie still comes home
 *  first-party via the callback's one-time handoff code consumed through the
 *  proxy — see sessionHandoff.ts. */
export const OAUTH_START_ORIGIN =
  (import.meta.env.VITE_OAUTH_ORIGIN as string | undefined) ??
  (import.meta.env.PROD ? 'https://careeropt-api.jellybop.workers.dev' : API_ORIGIN);

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** The browser's own IANA timezone, sent on every request so the server
 *  grounds "now" in the user's zone rather than guessing from edge geo.
 *  Mirrors apps/web/src/lib/api.ts browserTimeZone(). */
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // /auth/* lives at the worker root (browser-navigated for OAuth); everything
  // else goes through API_BASE (/api/*) on the same origin.
  const fullPath = path.startsWith('/auth/') ? `${API_ORIGIN}${path}` : `${API_BASE}${path}`;
  const tz = browserTimeZone();

  const response = await fetch(fullPath, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(tz ? { 'X-Time-Zone': tz } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'unknown' }));
    const message =
      typeof body === 'object' && body && 'message' in body && typeof body.message === 'string'
        ? body.message
        : typeof body === 'object' && body && 'error' in body && typeof body.error === 'string'
          ? body.error
          : response.statusText;
    throw new ApiError(response.status, message, body);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Mint a Studio voice-session conversation token + grounding. Identical wire
 * contract to apps/web's startAgentSession (apps/web/src/lib/api.ts) — same
 * route, same requireAuth-gated backend, same AgentSessionResponse shape —
 * so a live session opened from Throughline behaves exactly like one opened
 * from the corpus canvas. Passed to useLivingConversation as its
 * `mintSession` argument.
 */
export async function startAgentSession(
  input: AgentSessionRequest = {},
): Promise<AgentSessionResponse> {
  return apiFetch<AgentSessionResponse>('/agent-session', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// =============================================================================
// CORPUS — the Refine tab's card canvas + import flow (B-3). Every field below
// is copied verbatim from apps/web/src/lib/api.ts; see that file's line
// numbers cited per-type below so a future reader can diff for drift.
// =============================================================================

/** Mirrors apps/web/src/lib/api.ts RoleRecord (L78-93) exactly — one row from
 *  the `roles` table. `isCurrent` is 0 | 1 (the DB's own representation), NOT
 *  a boolean — do not coerce it here, the canvas reads it as the server sends it. */
export interface RoleRecord {
  id: string;
  userId: string;
  company: string;
  title: string;
  location: string | null;
  employerDescriptor: string | null;
  startDate: string;
  endDate: string | null;
  isCurrent: number;
  summary: string | null;
  sortOrder: number;
  metadata: unknown;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors apps/web/src/lib/api.ts AchievementRecord (L95-124) exactly — one
 *  row from the `achievements` table (a card). `roleEventId` is the sub-event
 *  link (e.g. the one FIFA employment that delivered both the Club World Cup
 *  2025 and the World Cup 2026) — null when the card sits directly on the role. */
export interface AchievementRecord {
  id: string;
  userId: string;
  roleId: string | null;
  roleEventId?: string | null;
  headline: string;
  situation: string | null;
  task: string | null;
  action: string | null;
  result: string | null;
  learning: string | null;
  kpiLabel: string | null;
  kpiValue: string | null;
  tags: string[] | null;
  voiceNoteR2Key: string | null;
  embedding: unknown;
  sortOrder: number;
  /** Rewrite-loop soft state: 'active' | 'merged' | 'archived'. Merged cards
   *  render greyed (processed); ARCHIVED cards (W3 remove-an-import) render in
   *  the collapsed archived section with an Undo. Both are excluded from the
   *  canvas's live groupings and from every serving read. */
  status: string;
  mergedInto: string | null;
  /** AXIS-P2 — the axis-affinity tags (AxisAffinityTaggerService.ts). Null
   *  when not yet tagged (the backfill's own target predicate); an EMPTY
   *  array is the honest "tagged, nothing traced" state, distinct from
   *  "never processed" — both render no chips, the distinction only matters
   *  server-side (the backfill's resume cursor). */
  axisAffinities?: AxisAffinityRecordApi[] | null;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors AxisAffinityTaggerService.ts's AxisAffinity shape verbatim.
 *  Re-exported under an API-local name (AxisAffinityChips.tsx's own
 *  AxisAffinityRecord is the canonical FE type; this is the wire shape). */
export interface AxisAffinityRecordApi {
  axisKey: string;
  score: number;
  source: 'model' | 'user';
  taxonomyVersion: number;
  taggedAt: string;
  evidence: string;
}

/** POST /corpus/axis-affinities/:subjectId/confirm — flips a model-sourced
 *  affinity to source:'user' (terminal). Returns the confirmed affinity. */
export async function confirmAxisAffinity(
  subjectId: string,
  subjectType: 'achievement' | 'facet',
  axisKey: string,
): Promise<AxisAffinityRecordApi> {
  const result = await apiFetch<{ ok: boolean; affinity: AxisAffinityRecordApi }>(
    `/corpus/axis-affinities/${encodeURIComponent(subjectId)}/confirm`,
    { method: 'POST', body: JSON.stringify({ subjectType, axisKey }) },
  );
  return result.affinity;
}

/** POST /corpus/axis-affinities/:subjectId/remove — strips the tag and
 *  writes a terminal exclusion so no future re-tag ever re-adds it. */
export async function removeAxisAffinity(
  subjectId: string,
  subjectType: 'achievement' | 'facet',
  axisKey: string,
): Promise<void> {
  await apiFetch<{ ok: boolean; removed: string }>(
    `/corpus/axis-affinities/${encodeURIComponent(subjectId)}/remove`,
    { method: 'POST', body: JSON.stringify({ subjectType, axisKey }) },
  );
}

/** GET /corpus/roles — apps/api/routes/corpus.routes.ts:1133. Server orders
 *  current-role-first then reverse-chronological; the canvas trusts that order. */
export async function getCorpusRoles(): Promise<RoleRecord[]> {
  const result = await apiFetch<{ roles: RoleRecord[] }>('/corpus/roles');
  return result.roles;
}

/** GET /corpus/achievements — apps/api/routes/corpus.routes.ts:1393. Already
 *  filtered server-side to non-superseded rows (a correction's old row never
 *  shows twice). */
export async function getCorpusAchievements(): Promise<AchievementRecord[]> {
  const result = await apiFetch<{ achievements: AchievementRecord[] }>('/corpus/achievements');
  return result.achievements;
}

/** Mirrors apps/web/src/lib/api.ts ImportResult (L126-142) — the response from
 *  POST /corpus/import. A single synchronous round trip (no polling/streaming):
 *  Claude extracts roles+achievements server-side, dedupes against the existing
 *  corpus, and returns counts. `sourceDocumentId` (FILE-DOOR, 2026-07-05) is
 *  present only when the import came through POST /corpus/import-file — the
 *  paste path never sets it. */
export interface ImportResult {
  ok: boolean;
  rolesCreated?: number;
  rolesMatched?: number;
  achievementsCreated?: number;
  achievementsSkipped?: number;
  kind?: 'cover_letter';
  archiveId?: string;
  themes?: string[];
  voiceSamplesCount?: number;
  whyCompanyCaptured?: boolean;
  tokens?: { input: number; output: number; cached: number; cacheHit: boolean };
  durationMs?: number;
  sourceDocumentId?: string;
  /** W3 remove-an-import: the corpus_imports event id for THIS import. The
   *  "imported N — undo this import" affordance round-trips it to
   *  archiveImport(). Present on both the paste and file doors going forward;
   *  absent on a cover-letter import (a narrative artifact, not corpus facts). */
  importId?: string;
}

/** POST /corpus/import — apps/api/routes/corpus.routes.ts:1698, ImportInput
 *  schema at L1570 (`text` must be >=20 chars; the server 400s otherwise — the
 *  caller shows that as the normal ApiError state). `source` mirrors apps/web's
 *  importPaste (useCorpus.ts:61); 'paste' is what Throughline's own paste-in
 *  textarea sends. documentDate defaults server-side to today.
 *
 *  FILE-DOOR (2026-07-05) added a SECOND door onto the exact same pipeline —
 *  see importCorpusFile below (POST /corpus/import-file) — for a PDF/DOCX
 *  upload. Both doors call the same server-side runCorpusImport function; this
 *  one stays the paste door, unchanged. */
export async function importCorpusText(
  text: string,
  source: 'linkedin' | 'cv' | 'resume' | 'paste' = 'paste',
  documentDate?: string,
): Promise<ImportResult> {
  return apiFetch<ImportResult>('/corpus/import', {
    method: 'POST',
    body: JSON.stringify({ source, text, documentDate }),
  });
}

/** Honest shape for POST /corpus/import-file's 422 no_text_layer response —
 *  a file that stored fine but yielded no usable text (a scanned/image-only
 *  PDF, a corrupt zip). Distinguished from a generic ApiError by callers via
 *  `instanceof FileImportNoTextError` so the FE can show the exact "paste
 *  instead" recovery path the brief's honesty law asks for, without string-
 *  matching a message. */
export class FileImportNoTextError extends Error {
  hint: string;
  sourceDocumentId?: string;
  constructor(hint: string, sourceDocumentId?: string) {
    super(hint);
    this.name = 'FileImportNoTextError';
    this.hint = hint;
    this.sourceDocumentId = sourceDocumentId;
  }
}

/** POST /corpus/import-file — apps/api/routes/corpus.importFile.routes.ts.
 *  multipart/form-data upload (field "file"); the server stores the ORIGINAL
 *  blob in R2, extracts its text (unpdf for PDF, fflate-unzip+XML for DOCX),
 *  and — if extraction clears the honest 200-char substantive floor — runs
 *  the SAME import-core the paste path uses (runCorpusImport server-side),
 *  returning the SAME ImportResult shape plus sourceDocumentId.
 *
 *  Deliberately bypasses apiFetch: that helper hardcodes
 *  'Content-Type': 'application/json' on every request, which would corrupt
 *  a multipart body (the browser must set its own boundary in Content-Type —
 *  same reasoning apps/api's ElevenLabsService.ts documents server-side for
 *  its own outgoing multipart calls). Everything else — same-origin
 *  credentials, the same ApiError shape for a non-2xx response — mirrors
 *  apiFetch exactly so callers don't need two different error-handling paths. */
export async function importCorpusFile(file: File): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(`${API_BASE}/corpus/import-file`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  const body = await response.json().catch(() => ({ error: 'unknown' }));

  if (!response.ok) {
    if (response.status === 422 && typeof body === 'object' && body && 'error' in body) {
      const b = body as { error: string; hint?: string; sourceDocumentId?: string };
      if (b.error === 'no_text_layer') {
        throw new FileImportNoTextError(
          b.hint ?? 'No readable text found in this file — paste the text instead.',
          b.sourceDocumentId,
        );
      }
    }
    const message =
      typeof body === 'object' && body && 'hint' in body && typeof body.hint === 'string'
        ? body.hint
        : typeof body === 'object' && body && 'error' in body && typeof body.error === 'string'
          ? body.error
          : response.statusText;
    throw new ApiError(response.status, message, body);
  }

  return body as ImportResult;
}

/** Mirrors apps/web/src/lib/api.ts RehomeSourceRef — the provenance stamped on
 *  the re-homed row. 'drag_drop' is the ONLY kind this app emits (Throughline
 *  has no live voice-confirm re-home path yet); the drop itself is the grounded
 *  act, so the destination gate's words-must-trace branch never runs for it
 *  (the route's KIND_INTENT_TO_GATE map, corpus.routes.ts:705). */
export interface RehomeSourceRef {
  kind: 'drag_drop' | 'spoken_correction';
  capturedFrom: string;
}

/** Mirrors apps/web/src/lib/api.ts RehomeRequest (L796-800) — the PUT body.
 *  Exactly one of targetRoleId / targetRoleEventId must be set (the server
 *  400s a request with neither — RehomeBody's .refine, corpus.routes.ts:723). */
export interface RehomeRequest {
  targetRoleId?: string;
  targetRoleEventId?: string;
  sourceRef: RehomeSourceRef;
}

/** Mirrors apps/web/src/lib/api.ts RehomeResult (L805-809). The full server
 *  response carries more (destinationGrounded, faithfulness, flagReason,
 *  facetsRepointed — corpus.routes.ts:790-798); the canvas only depends on
 *  `ok`/`newId`/`supersededId`, same as apps/web's own FE type. */
export interface RehomeResult {
  ok: boolean;
  newId: string;
  supersededId: string;
}

/**
 * Re-home a card to a corrected era via the §6 supersede (the correction loop).
 * Identical wire contract to apps/web/src/lib/api.ts rehomeAchievement (L825-830):
 * PUT /corpus/achievements/:id/rehome, same body shape, same result shape.
 *
 * NOTE (drag pattern, deliberate deviation from apps/web): apps/web's own drag
 * handler (Corpus.tsx:404-484, via useCorpus.ts:260-268) does NOT move the card
 * locally — it fires the PUT and waits for a full corpus refresh to show the
 * corrected truth ("THIN PRESENTATION... we do NOT optimistically move the card").
 * The brief for THIS tab explicitly asks for optimistic UI + server-confirm +
 * error rollback, so the Refine canvas's drag handler (see CorpusCanvas.tsx)
 * moves the card immediately, calls this function, re-fetches on success (so the
 * served truth still wins), and restores the pre-drag snapshot on failure. This
 * function itself is unchanged from apps/web's contract either way — only the
 * caller's local-state discipline differs.
 */
export async function rehomeAchievement(id: string, body: RehomeRequest): Promise<RehomeResult> {
  return apiFetch<RehomeResult>(`/corpus/achievements/${id}/rehome`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// =============================================================================
// ROLE + ACHIEVEMENT FIELD EDITING (LANE ROLE-REFINE) — parity matrix gaps #6/#7:
// the PATCH routes below already existed server-side (apps/web's EditRoleSheet /
// EditAchievementSheet call the identical contracts); Throughline had no client
// wired to them at all. These two functions are the thin client, nothing more —
// same shape apps/web already proved, no new field, no new route.
// =============================================================================

/** The user-editable subset of a role's fields — mirrors the server's
 *  `RolePatch` zod schema exactly (apps/api/routes/corpus.routes.ts:1200-1216):
 *  every key optional (a caller sends only what changed), dates as plain
 *  YYYY-MM-DD strings, `isCurrent` as a real boolean here (the route coerces
 *  it to the DB's 0/1 server-side — this client never sends the raw int). */
export interface RolePatch {
  company?: string;
  title?: string;
  location?: string | null;
  employerDescriptor?: string | null;
  startDate?: string;
  endDate?: string | null;
  isCurrent?: boolean;
  summary?: string | null;
}

/** PATCH /corpus/roles/:id — apps/api/routes/corpus.routes.ts:1218. Returns the
 *  full updated row (server-confirmed truth); 404s as `{ error: 'not_found' }`
 *  if the id doesn't belong to the caller. */
export async function updateRole(id: string, patch: RolePatch): Promise<RoleRecord> {
  const result = await apiFetch<{ role: RoleRecord }>(`/corpus/roles/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return result.role;
}

/** The user-editable subset of an achievement's fields — mirrors the server's
 *  `AchievementPatch` zod schema exactly (apps/api/routes/corpus.routes.ts:
 *  1472-1483). `tags` replaces the whole array when sent (matches the route's
 *  own `updates.tags = parse.data.tags` — no partial-array merge server-side). */
export interface AchievementPatch {
  roleId?: string | null;
  headline?: string;
  situation?: string | null;
  task?: string | null;
  action?: string | null;
  result?: string | null;
  learning?: string | null;
  kpiLabel?: string | null;
  kpiValue?: string | null;
  tags?: string[];
}

/** PATCH /corpus/achievements/:id — apps/api/routes/corpus.routes.ts:1485.
 *  Returns the full updated row; 404s as `{ error: 'not_found' }` if the id
 *  doesn't belong to the caller. */
export async function updateAchievement(
  id: string,
  patch: AchievementPatch,
): Promise<AchievementRecord> {
  const result = await apiFetch<{ achievement: AchievementRecord }>(`/corpus/achievements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return result.achievement;
}

// =============================================================================
// W3 REMOVE-AN-IMPORT — the archive/undo client. His live test: "Need a way to
// delete import if it's the wrong file like this one." Every call is a SOFT
// archive (server-side status='archived' / metadata.archived=true — never a
// hard delete), each with a matching undo. THE GOVERNING LAW: user-driven — the
// client never guesses which rows are wrong; his selection (or his tap on
// "undo this import") is the authority.
// =============================================================================

/** Counts of what an archive/unarchive touched (mirrors the server's
 *  SelectionArchiveResult / ImportArchiveResult). */
export interface CorpusArchiveResult {
  ok: boolean;
  rolesArchived: number;
  achievementsArchived: number;
}

/** POST /corpus/archive-selection — soft-archive a hand-picked set of roles +
 *  achievements (the corpus-management surface: pick the wrong rows → archive).
 *  This is the path for HIS existing wrong import (its rows predate the import_id
 *  back-link, so there is nothing to key on but his selection). */
export async function archiveCorpusSelection(selection: {
  roleIds?: string[];
  achievementIds?: string[];
}): Promise<CorpusArchiveResult> {
  return apiFetch<CorpusArchiveResult>('/corpus/archive-selection', {
    method: 'POST',
    body: JSON.stringify(selection),
  });
}

/** POST /corpus/unarchive-selection — the reverse (the "undo" from the archived
 *  section, per row or in bulk). */
export async function unarchiveCorpusSelection(selection: {
  roleIds?: string[];
  achievementIds?: string[];
}): Promise<CorpusArchiveResult> {
  return apiFetch<CorpusArchiveResult>('/corpus/unarchive-selection', {
    method: 'POST',
    body: JSON.stringify(selection),
  });
}

/** POST /corpus/imports/:importId/archive — undo a WHOLE import (the provenance
 *  path, new imports only): archive the event + every role/achievement it
 *  seeded. `importId` comes straight from the ImportResult of a fresh import. */
export async function archiveImport(importId: string): Promise<CorpusArchiveResult> {
  return apiFetch<CorpusArchiveResult>(`/corpus/imports/${importId}/archive`, { method: 'POST' });
}

/** POST /corpus/imports/:importId/unarchive — the reverse of archiveImport. */
export async function unarchiveImport(importId: string): Promise<CorpusArchiveResult> {
  return apiFetch<CorpusArchiveResult>(`/corpus/imports/${importId}/unarchive`, { method: 'POST' });
}

// =============================================================================
// ALIGNMENT REVIEW (CLEANUP-CREW) — the achievement-alignment review deck's
// data + write path. Mirrors apps/api/routes/alignment.routes.ts's wire
// contract exactly (GET /corpus/alignment-review, POST /corpus/alignment-
// review/:achievementId/rule). THE GOVERNING LAW: a ruling is USER-DRIVEN and
// TERMINAL — this client never batches or auto-applies a ruling; every call
// is one card, one explicit tap.
// =============================================================================

/** One detected signal on a flagged achievement — mirrors AlignmentSignal
 *  (apps/api/services/corpus/AlignmentReviewService.ts). `evidence` is
 *  always the ACTUAL matched text/value, never a paraphrase (NO INVENTION —
 *  the card shows the user exactly what tripped the flag). */
export interface AlignmentSignal {
  kind:
    | 'cross_employer_leakage'
    | 'domain_vocab_mismatch'
    | 'era_mismatch'
    | 'capture_context_mismatch'
    | 'near_duplicate';
  evidence: string;
  suggestedRoleId?: string;
  /** ADDITIVE, 'near_duplicate' ONLY (RESUME-POLISH lane, part c): every
   *  OTHER achievement id + its real, verbatim headline in this card's
   *  cluster — the deck's evidence for "these N read like the same story."
   *  Absent for every other signal kind. */
  clusterMembers?: { achievementId: string; headline: string }[];
}

/** A minimal role summary the review card renders ("filed under: X" / the
 *  suggested destination) — deliberately narrower than the full RoleRecord,
 *  since the card only ever displays company + title. */
export interface AlignmentRoleSummary {
  id: string;
  company: string;
  title: string;
}

/** One flagged achievement, enriched server-side with everything the card
 *  needs to render without a second round trip — mirrors alignment.routes
 *  .ts's FlagResponseItem exactly. */
export interface AlignmentFlag {
  achievementId: string;
  headline: string;
  currentRole: AlignmentRoleSummary | null;
  signals: AlignmentSignal[];
  suggestedRole: AlignmentRoleSummary | null;
}

/** GET /corpus/alignment-review — apps/api/routes/alignment.routes.ts.
 *  Computed ON DEMAND (no model call, no caching) over the authed user's
 *  active corpus; an empty `flags` array is the honest common case (most
 *  cards are filed correctly) — the FE renders NOTHING when this is empty,
 *  never a fabricated "all clear" banner standing in for real signal. */
export async function getAlignmentReview(): Promise<{ flags: AlignmentFlag[] }> {
  return apiFetch<{ flags: AlignmentFlag[] }>('/corpus/alignment-review');
}

/** The four terminal rulings a user can make on a flagged suspect. 'merged'
 *  is additive (RESUME-POLISH lane) — the 'near_duplicate' signal's MERGE
 *  action. */
export type AlignmentRulingValue = 'moved' | 'kept' | 'flagged_split' | 'merged';

export interface AlignmentRuleRequest {
  ruling: AlignmentRulingValue;
  /** Required when ruling === 'moved' (the server 400s otherwise). */
  movedToRoleId?: string;
  /** Required when ruling === 'merged' (the server 400s otherwise) — the
   *  SURVIVOR achievement this card is merged INTO (the richest cluster
   *  member — the deck computes this the SAME way the résumé composer picks
   *  a survivor, via pickRichestRepresentative's identical rule: most chars,
   *  tie-broken by most numeric facts). */
  mergeIntoAchievementId?: string;
  /** The evidence signals the user was actually shown — sent back verbatim
   *  so the ruling ledger freezes what a past ruling was made against, even
   *  if the detector's heuristics change later. */
  evidenceSnapshot?: AlignmentSignal[];
}

export interface AlignmentRuleResult {
  ok: boolean;
  ruling: AlignmentRulingValue;
  /** Present only when ruling === 'moved' — the §6 supersede's minted ids
   *  (the achievement got a NEW id; the OLD id is preserved, superseded). */
  newId?: string;
  supersededId?: string;
  /** Present only when ruling === 'merged' — the card that was soft-merged
   *  away (mirrors this achievementId, echoed back for confirmation) + the
   *  survivor it was merged into. */
  mergedId?: string;
  survivorId?: string;
}

/** POST /corpus/alignment-review/:achievementId/rule. A 'moved' ruling
 *  re-homes the card through the EXISTING §6 supersede server-side (the
 *  SAME write path the drag-rehome canvas already uses) — this client never
 *  moves anything itself, it only sends the human's decision. */
export async function ruleAlignmentFlag(
  achievementId: string,
  body: AlignmentRuleRequest,
): Promise<AlignmentRuleResult> {
  return apiFetch<AlignmentRuleResult>(`/corpus/alignment-review/${achievementId}/rule`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// =============================================================================
// RÉSUMÉ (the job-free corpus résumé) — the Refine tab's "your corpus, as a
// résumé" view (this lane's wiring target #1). Mirrors apps/web/src/lib/
// api.ts's getCorpusResume verbatim: SAME GET /corpus/resume route (read-fresh-
// or-compose, apps/api/routes/corpus.routes.ts:130), SAME {document} envelope,
// SAME ResumeDocument contract (packages/schema/src/resume_document.ts) both
// apps import from the identical schema package — no drift between apps/web's
// established format and this one. The backend (ResumeComposerService +
// BulletGhostwriteService) synthesises the ordering + profile prose + skills
// + grounded ghostwritten bullets; this client never derives any of that
// shape (NO INVENTION — composition is the server's job).
// =============================================================================

/** 'fresh': a written cut exists and matches the CURRENT corpus epoch — serve
 *  it by default. 'stale': a written cut exists but the corpus moved since —
 *  offer a re-write, do not silently serve the outdated prose. 'none': never
 *  written — show the raw cut + the write invite. Computed server-side,
 *  read-only, zero model calls (POLISH-2, living-résumé Phase A). */
export type WrittenCutFreshness = 'fresh' | 'stale' | 'none';

/** GET /corpus/resume — apps/api/routes/corpus.routes.ts:130. requireAuth-
 *  gated, same Lucia cookie session every other route in this app uses.
 *  Read-fresh-or-compose: serves the latest stored corpus résumé when the
 *  corpus hasn't changed since it was minted, else composes + gates a fresh
 *  one. A 503 (`no_anthropic_key`) propagates as an ApiError, same as every
 *  other Claude-backed route in this file.
 *
 *  WIDENED (Save to Drive re-land, 2026-07-05): the server has ALWAYS
 *  returned `resumeId` (both the serve-from-store and fresh-compose branches
 *  insert/read a real `resumes` row and include its id) — this client type
 *  was just narrower than the wire contract, silently dropping the field.
 *  Purely additive: every existing caller that destructures only `document`
 *  is byte-identical; `resumeId` is the real resumes.id, the artifactId
 *  DriveSaveButton needs — never a client-invented value.
 *
 *  WIDENED AGAIN (POLISH-2, 2026-07-05): `writtenCut` is additive — the
 *  server's read-only freshness check of the SEPARATE written-cut row (never
 *  minted by this GET; only POST /corpus/resume/write mints one). `written`
 *  carries that row's OWN {resumeId, document} ONLY when writtenCut==='fresh'
 *  (a pure read alongside the raw compose — zero extra model calls, zero
 *  extra round-trip), so the panel can show the written cut as the DEFAULT
 *  view without a second fetch or an explicit tap. Existing callers that
 *  don't read either field are byte-identical. */
export async function getCorpusResume(): Promise<{
  document: ResumeDocument;
  resumeId: string;
  writtenCut?: WrittenCutFreshness;
  written?: { resumeId: string; document: ResumeDocument } | null;
}> {
  return apiFetch<{
    document: ResumeDocument;
    resumeId: string;
    writtenCut?: WrittenCutFreshness;
    written?: { resumeId: string; document: ResumeDocument } | null;
  }>('/corpus/resume');
}

/** POST /corpus/resume/write — apps/api/routes/corpus.routes.ts. Generates
 *  (or regenerates, on a stale/absent written row) the WRITTEN cut: the SAME
 *  raw corpus résumé, with its profile line + role descriptors run through
 *  the arc-guided craft pass (CorpusResumeWriteService), gated by the same
 *  grounding + lint discipline the raw cut's bullets already pass. Persists a
 *  NEW resumes row (cutKind:'written'); the NEXT getCorpusResume() call finds
 *  writtenCut:'fresh' with zero further model calls. Entitlement-gated
 *  ('tailoring', same family as job-tailoring generation) + rate-limited
 *  ('ai_expensive', same tier as /jobs/:id/tailor) — a real Claude call, never
 *  free. NEVER called automatically: only the explicit "write it properly"
 *  tap in CorpusResumePanel invokes this — the loop the invite re-runs. */
export async function postCorpusResumeWrite(): Promise<{
  document: ResumeDocument;
  resumeId: string;
  writtenCut: WrittenCutFreshness;
}> {
  return apiFetch<{ document: ResumeDocument; resumeId: string; writtenCut: WrittenCutFreshness }>(
    '/corpus/resume/write',
    { method: 'POST', body: '{}' },
  );
}

// =============================================================================
// TAILORED RÉSUMÉ (per job) — GAP 1: JobDetail's missing tailored-résumé
// document. Ported verbatim from apps/web/src/lib/api.ts's buildResume /
// listResumeVersions (L1116-1128) — SAME POST /jobs/:id/resume and GET
// /jobs/:id/resumes routes (jobs.routes.ts:1392-1509/1512, LOCKED, unedited by
// this lane), SAME request/response shapes. Deterministic composer (no LLM
// call on this path — ResumeComposerService), NOT entitlement-gated, persists
// an immutable `resumes` row per build. `lintGate`/`blocked` are additive,
// present only when the server's own render-blocking gate over the CV text
// failed (mirrors TailorResponse's identical additive-lintGate convention
// above) — reuses TailorLintGate's shape verbatim since LintGateService's
// `LintGate` (variant/passed/blockingCount/blocks) is the SAME gate both
// routes run, just over different text blocks.
// =============================================================================

export interface BuildResumeResponse {
  ok: boolean;
  resumeId: string;
  scenarioId: string;
  scenarioName: string;
  document: ResumeDocument;
  /** ATS-clean plain text (shared pure renderer) — ready for a "copy as text"
   *  affordance without re-deriving it from `document` client-side. */
  rendered: { text: string };
  linterReport: ResumeLinterReport;
  /** Present only on a FAILED lint gate (byte-identical response otherwise). */
  lintGate?: TailorLintGate;
  blocked?: boolean;
}

export interface ResumeVersion {
  id: string;
  label: string;
  formatVariant: string;
  pages: number | null;
  wordCount: number | null;
  linterReport: ResumeLinterReport | null;
  createdAt: string;
}

/** POST /jobs/:id/resume — jobs.routes.ts:1392. Builds a tailored CV for this
 *  job under the resolved scenario lens, pulling the latest tailored CvBrief
 *  (if a tailor has run) to drive the summary + achievement ordering. Persists
 *  a new immutable `resumes` row every call (never an overwrite) — the caller
 *  re-lists versions after a successful build. */
export async function buildResume(
  jobId: string,
  args: { scenarioId?: string; chosenAchievementIds?: string[] } = {},
): Promise<BuildResumeResponse> {
  return apiFetch<BuildResumeResponse>(`/jobs/${jobId}/resume`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

/** GET /jobs/:id/resumes — jobs.routes.ts:1512. Newest-first version list for
 *  this job. An empty array is the honest "never built one for this job yet"
 *  state, not an error. */
export async function listResumeVersions(jobId: string): Promise<{ versions: ResumeVersion[] }> {
  return apiFetch<{ versions: ResumeVersion[] }>(`/jobs/${jobId}/resumes`);
}

// =============================================================================
// DNA OVERLAY — the woven thread the corpus reads over the cards (this lane's
// wiring target #2, "threads Mira is noticing"). Mirrors apps/web/src/lib/
// api.ts's DnaOverlay/DnaThreadRecord/getCorpusDnaOverlay verbatim: SAME GET
// /corpus/dna-overlay route (read-only, no LLM — apps/api/routes/
// corpus.routes.ts:69, backed by ProbeService.loadOverlay), SAME response
// shape, verified directly against loadOverlay's own return type
// (apps/api/services/dna/ProbeService.ts:91-98, 109-163).
//
// `profile` is null when the user has never run a DNA-synthesis pass yet (no
// dna_profiles row) — an honest "no signal yet" state, never a fabricated
// placeholder. `threads` is the era-to-era continuity the synthesis found
// (dna_profiles.threads jsonb, packages/schema/src/dna.ts:127): each entry's
// `thread` is a one-line phrase describing what carries across two eras
// (dna/SynthesisEngine.ts's own prompt: "the same refusal to let a system fall
// over" is the shape of a real one) — the exact grounded replacement for the
// static placeholderRefine.threads pair Refine.tsx rendered unconditionally.
// =============================================================================

export interface DnaThreadRecord {
  from: string;
  to: string;
  thread: string;
}

export interface DnaOverlay {
  profile: {
    // Additive (LANE DNA-BUILD, Phase 0 backend fix): the server's
    // ProbeService.loadOverlay was silently dropping this field even though
    // dna_profiles.origins already carried real synthesis output — see that
    // file's own doc comment on the fix. Was missing on this FE type for the
    // same reason it was missing on apps/web's (both types simply mirrored
    // the server's, which had the gap); now present on both sides.
    origins: string | null;
    throughLine: string | null;
    drivers: string | null;
    positioningSpine: string | null;
    threads: DnaThreadRecord[] | null;
  } | null;
  // Type tightened from Record<string, unknown[]> (LANE DNA-BUILD, Phase 2):
  // the server's own OverlayFacet contract (apps/api/services/dna/
  // ProbeService.ts:79-89) is exactly the DnaFacetRecord shape declared below
  // in this file — Phase 2's throughLineEras derivation (Interview.tsx) reads
  // .isNormalToMe/.id/.content off these rows to build the timeline's gold
  // pills, so the honest, already-true type is named here rather than routed
  // through an `unknown[]` cast at the call site. Additive/type-only — no
  // wire-shape change, no behavior change for any existing consumer (nothing
  // in throughline read past `unknown[]` before this).
  facetsByRole: Record<string, DnaFacetRecord[]>;
}

/** GET /corpus/dna-overlay — apps/api/routes/corpus.routes.ts:69. requireAuth-
 *  gated, same Lucia cookie session every other route in this app uses.
 *  Read-only (no LLM call, no audit-log row) — safe to call on every mount. */
export async function getCorpusDnaOverlay(): Promise<DnaOverlay> {
  return apiFetch<DnaOverlay>('/corpus/dna-overlay');
}

// =============================================================================
// DNA INTERVIEW — the turn-based excavation loop (LANE DNA-BUILD, Phase 1).
// Ported verbatim from apps/web/src/lib/api.ts (same backend, same wire
// contract, same verbatim-mirror discipline the DnaOverlay port above already
// used — that file's own header comment names this exact precedent to
// follow). Every shape below matches apps/web/src/lib/api.ts:515-587,680-713
// field-for-field: DnaPhase/DnaSessionRecord/DnaFacetRecord/DnaEra/
// DnaTurnResult/InputCapture, plus the 4 routes (createDnaSession/
// getCurrentDnaSession/dnaTurn/synthesiseDna). The engines behind these
// routes (InterviewTurnEngine, DistillationEngine + the grounding gate,
// SynthesisEngine) already exist and are already live in production via
// apps/web — nothing on the server changes for this port. Session-driving
// mutations (POST /sessions, /turn, /synthesise, /transcribe) are gated
// server-side by requireEntitlement('voice_interview')
// (apps/api/routes/dna.routes.ts:56-68); a user lacking it sees the server's
// own typed error surface through apiFetch's existing ApiError path, same as
// every other entitlement-gated route in this file — no separate client gate.
// =============================================================================

export type CaptureSource = 'voice' | 'text' | 'dialogue';

export interface InputCapture {
  text: string;
  source: CaptureSource;
  capturedAt?: string;
  meta?: Record<string, unknown>;
}

export interface DnaTurnRecord {
  role: 'assistant' | 'user';
  text: string;
  turnIndex: number;
  at: string;
  audioR2Key?: string;
}

export type DnaPhase = 'origins' | 'timeline' | 'synthesis' | 'complete';

export interface DnaSessionRecord {
  id: string;
  userId: string;
  phase: DnaPhase;
  currentRoleId: string | null;
  transcript: DnaTurnRecord[] | null;
  interviewVoiceId: string | null;
  statesideVoiceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type DnaFacetType = 'accomplishment' | 'knowledge' | 'lesson' | 'thread' | 'driver';

export interface DnaFacetRecord {
  id: string;
  facetType: DnaFacetType;
  content: string;
  isNormalToMe: number;
  roleId: string | null;
  originLabel: string | null;
  turnIndex: number | null;
  linkedAchievementId: string | null;
  confidence: number | null;
}

export interface DnaEra {
  roleId: string | null;
  label: string;
  company?: string;
  title?: string;
  startDate?: string;
  endDate?: string | null;
}

export interface DnaProfileRecord {
  id: string;
  origins: string | null;
  drivers: string | null;
  throughLine: string | null;
  threads: DnaThreadRecord[] | null;
  positioningSpine: string | null;
  version: number;
  createdAt: string;
}

export interface DnaTurnResult {
  nextQuestion: string;
  facets: DnaFacetRecord[];
  goldSpotted: boolean;
  phase: DnaPhase;
  advanceEra: boolean;
}

/** POST /dna/sessions — apps/api/routes/dna.routes.ts:224. Starts a session;
 *  generates a corpus-anchored opener (InterviewTurnEngine, Opus), falls back
 *  to a fixed ORIGINS_OPENER server-side if that call fails. Entitlement-gated
 *  ('voice_interview'). */
export async function createDnaSession() {
  return apiFetch<{ session: DnaSessionRecord; firstQuestion: string; eras: DnaEra[] }>(
    '/dna/sessions',
    { method: 'POST', body: '{}' },
  );
}

/** GET /dna/sessions/current — apps/api/routes/dna.routes.ts:291. Resume: the
 *  live session (or null if none), its banked facets, the latest synthesised
 *  profile (or null), and the user's eras. Open read, no entitlement gate. */
export async function getCurrentDnaSession() {
  return apiFetch<{
    session: DnaSessionRecord | null;
    facets: DnaFacetRecord[];
    profile: DnaProfileRecord | null;
    eras?: DnaEra[];
  }>('/dna/sessions/current');
}

/** POST /dna/sessions/:id/turn — apps/api/routes/dna.routes.ts:419. The loop:
 *  DistillationEngine (Haiku) extracts facets, each accomplishment claim runs
 *  through the grounding gate (gateClaim — NO INVENTION enforced in code, not
 *  prompt) and is banked or flagged (never dropped), InterviewTurnEngine
 *  (Opus) asks the next question and decides phase/era advance. Entitlement-
 *  gated ('voice_interview'). */
export async function dnaTurn(sessionId: string, capture: InputCapture) {
  return apiFetch<DnaTurnResult>(`/dna/sessions/${sessionId}/turn`, {
    method: 'POST',
    body: JSON.stringify({ capture }),
  });
}

/** POST /dna/sessions/:id/synthesise — apps/api/routes/dna.routes.ts:814. All
 *  facets + transcript → SynthesisEngine (Opus) → the DNA; lints the prose
 *  against the user's resolved English variant before persisting. Entitlement-
 *  gated ('voice_interview'). */
export async function synthesiseDna(sessionId: string) {
  return apiFetch<{ profile: DnaProfileRecord; lint: unknown }>(
    `/dna/sessions/${sessionId}/synthesise`,
    { method: 'POST', body: '{}' },
  );
}

// =============================================================================
// PRESENCE (Get Found) — M5 prewarm-on-mount for the Profiles tab, plus the
// Wave-1 C1 real-data reads (getPlatforms / listSavedProfiles). The Get
// Found module itself (apps/api/routes/presence.routes.ts, apps/web/src/pages/
// Presence.tsx) is LOCKED (Jason, 2026-06-24) — every function here calls its
// EXISTING, already-shipped routes verbatim (byte-identical wire contract to
// apps/web/src/lib/api.ts's own getTargetRoles/prewarmPresence/getPlatforms/
// listSavedProfiles) and makes ZERO backend changes. getTargetRoles/
// prewarmPresence: a user who lands on Profiles with a real corpus + a saved
// target-role family is one click from Generate, so this app should warm the
// SAME server-side bridge KV cache apps/web's Presence.tsx already warms on
// its own mount — the cache is keyed on {corpusEpoch, userId,
// targetRoleFamily}, not on which frontend called it, so a warm from either
// app satisfies the other's eventual /presence/generate. getPlatforms/
// listSavedProfiles: the Get Found score/profile chips this app's Profiles
// tab renders (C1) come from these, NEVER from placeholderData — a saved
// profile's `score` is null until validated criteria exist for that platform
// (see PresenceOptimisation's doc comment in apps/web/src/lib/api.ts), and an
// empty `profiles` array is the honest "never generated one yet" state, not
// an error.
// =============================================================================

/** The user's target roles for Get Found, served from the DB (never
 *  hardcoded). Mirrors apps/web/src/lib/api.ts's getTargetRoles exactly —
 *  same GET /presence/target-roles route, same {roles: string[]} shape. */
export async function getTargetRoles(): Promise<string[]> {
  const r = await apiFetch<{ roles: string[] }>('/presence/target-roles');
  return r.roles;
}

/**
 * G0 — persist the user's target roles (called on every add/remove edit).
 * Ported verbatim from apps/web/src/lib/api.ts's saveTargetRoles (L1406-1412)
 * — SAME PUT /presence/target-roles route, SAME {roles} body, SAME
 * {roles: string[]} response shape. The server (presence.routes.ts:1123,
 * LOCKED, unedited by this lane) trims + dedupes case-insensitively and
 * jsonb_sets desiredRoles — the DB stays the single source of truth; this
 * client only ever forwards the list the user asked for, never invents one.
 */
export async function saveTargetRoles(roles: string[]): Promise<string[]> {
  const r = await apiFetch<{ roles: string[] }>('/presence/target-roles', {
    method: 'PUT',
    body: JSON.stringify({ roles }),
  });
  return r.roles;
}

/** R125 — warm the server's bridge cache before the user ever opens Generate.
 *  Mirrors apps/web/src/lib/api.ts's prewarmPresence exactly — same POST
 *  /presence/prewarm route, same body shape. Fire-and-forget; the server
 *  decides whether anything needs warming (empty corpus / already-cached /
 *  no key configured all resolve to a quiet `{warmed:false}`, never a thrown
 *  error the caller must handle). */
export function prewarmPresence(targetRoleFamily: string[]): Promise<{ warmed: boolean }> {
  return apiFetch<{ warmed: boolean }>('/presence/prewarm', {
    method: 'POST',
    body: JSON.stringify({ targetRoleFamily }),
  });
}

/** One profile-buildable platform the lake knows about, with how many rules
 *  back it. Served — the FE holds NO hardcoded platform list. Mirrors
 *  apps/web/src/lib/api.ts's PresencePlatformProfile exactly (that file's
 *  L1655-1662); only the `criteria` breakdown fields this app actually
 *  renders are kept typed strictly, same trimming discipline this file's
 *  TailorBrevity/TailorLintBlock types already use above. */
export interface PresencePlatformProfile {
  platform: string;
  ruleCount: number;
  criteria?: { scored: number; guaranteed: number; advisory: number };
}

/** GET /presence/platforms — apps/web/src/lib/api.ts's getPlatforms (L1675),
 *  same shape verbatim. Drives the Profiles tab's platform chips; this app
 *  holds no hardcoded platform array either. */
export async function getPlatforms(): Promise<{
  profiles: PresencePlatformProfile[];
  atsParsers: PresencePlatformProfile[];
  universals: number;
}> {
  return apiFetch('/presence/platforms');
}

/** One row in the saved-versions list (GET /presence/profiles → `profiles[]`).
 *  Mirrors apps/web/src/lib/api.ts's SavedProfileSummary exactly (L1598-1608).
 *  `score` is null when the platform was unvalidated at save time — never a
 *  fabricated number; this app's Profiles tab renders that as an honest
 *  "not yet scored" rather than defaulting to 0. */
export interface SavedProfileSummary {
  id: string;
  platform: string;
  label: string | null;
  targetRoles: string[];
  englishVariant: string | null;
  score: number | null;
  createdAt: string;
}

/** GET /presence/profiles — apps/web/src/lib/api.ts's listSavedProfiles
 *  (L1642), same shape verbatim. Newest-first (server-ordered); an empty
 *  array is the honest "never generated + saved a profile yet" state — this
 *  app renders that as a cold-state prompt to generate one, never as an error. */
export async function listSavedProfiles(): Promise<{ profiles: SavedProfileSummary[] }> {
  return apiFetch<{ profiles: SavedProfileSummary[] }>('/presence/profiles');
}

/** A generated platform profile's rendered content. Mirrors apps/web/src/lib/
 *  api.ts's PresenceProfileResult (L1430-1456). LANE JOB-TRUST G5 widened this
 *  from the prior headline/about/skills-only trim to include `lint` — the
 *  brief's honest lint-chip render needs it, and it is NOT genuinely
 *  optional on the wire (PresenceProfile's server-side contract always
 *  computes it; apps/web's own type declares it required, non-optional).
 *
 *  GAP 3 (docs/ARCHITECTURE-honesty-map.md wiring audit) widens this AGAIN to
 *  add `experienceBlocks`/`paste` — the server (presence.routes.ts's POST
 *  /presence/generate, ~:1069) has ALWAYS computed + returned `paste` inside
 *  `profile` (renderPresencePaste, packages/schema/src/render/
 *  presence_paste.ts — gated/re-rendered post-title-gate so it can never carry
 *  a nulled/invented title); this app just never typed it, so
 *  saveSavedProfile's `profile: result.profile` passthrough (Profiles.tsx)
 *  was ALREADY forwarding it at runtime (apiFetch does no schema stripping,
 *  see apiFetch's own plain response.json() below) — this change only makes
 *  it something this file's code can finally READ. `paste` stays OPTIONAL
 *  (`?`) because an older saved row (predating this widening) or a
 *  generate response an old cached build served may genuinely lack it —
 *  never assume it, always fall back to the raw field when absent (the SAME
 *  `p.paste?.field ?? p.field` idiom apps/web/src/pages/Presence.tsx already
 *  establishes at every copy-button call site). */
export interface PresenceProfileResult {
  platform: string;
  recommendedTitles: string[];
  headline: string;
  about: string;
  experienceBlocks?: { role: string; bullets: string[] }[];
  skills: string[];
  lint: {
    au: { passing: boolean; totalIssues: number };
    sellShort: { passing: boolean };
    brevity: { withinBudget: boolean; wordCount: number };
  };
  /** Server-rendered, PLATFORM-SHAPED copy-paste strings (renderPresencePaste,
   *  lake-driven per platform_format) — GAP 3's whole point: this app renders
   *  THESE for the copy buttons instead of re-gluing shaping client-side.
   *  `full` is the whole profile as one platform-ordered block ("copy all"). */
  paste?: PresencePaste;
}

/** A recalled saved version in full (GET /presence/profiles/:id → `profile`).
 *  Mirrors apps/web/src/lib/api.ts's SavedProfileFull exactly (L1614-1627).
 *  LANE JOB-TRUST G3 widens `optimisation` from `unknown` to the real served
 *  PresenceOptimisation shape (now that this file declares the full
 *  criteria[] breakdown below, the trimmed-type reason `unknown` used to
 *  document no longer applies) — the recalled-profile OptimisationReadout
 *  render (Profiles.tsx) needs the SAME per-criterion detail the fresh-
 *  generate path gets, never a second-class summary-only view. */
export interface SavedProfileFull {
  id: string;
  userId: string;
  platform: string;
  label: string | null;
  targetRoles: string[];
  englishVariant: string | null;
  profile: PresenceProfileResult;
  skills: string[];
  /** The PlatformOptimisation captured at save time, or null (an older saved
   *  row predating this field, or a save under an unvalidated platform). */
  optimisation: PresenceOptimisation | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /presence/profiles/:id — apps/web/src/lib/api.ts's recallSavedProfile
 *  (L1647), same shape verbatim. Used to load the REAL headline/
 *  recommendedTitles text for the most-recently-saved profile — the lean
 *  SavedProfileSummary list row deliberately omits these (server-side "lean
 *  by design," per that type's own doc comment), so a genuine preview of
 *  what Get Found actually produced needs this one extra call. */
export async function recallSavedProfile(id: string): Promise<{ profile: SavedProfileFull }> {
  return apiFetch<{ profile: SavedProfileFull }>(`/presence/profiles/${encodeURIComponent(id)}`);
}

/** The request body for POST /presence/profiles — mirrors apps/web/src/lib/
 *  api.ts's SaveProfileInput (L1583-1593) exactly. The optimisation is NOT
 *  sent: the server recomputes + persists it (never trust a client score). */
export interface SaveProfileInput {
  platform: string;
  targetRoles: string[];
  englishVariant?: string;
  /** The full generated profile (the /generate `profile` payload). */
  profile: PresenceProfileResult;
  /** The skill set (denormalised for query/filter; also inside `profile`). */
  skills: string[];
  /** Optional user label for this version. */
  label?: string;
}

/** POST /presence/profiles — apps/web's saveSavedProfile (L1632), same shape
 *  verbatim; EXISTING LOCKED route, called not edited. WIRE-AUDIT fix #4: the
 *  generate flow previously never saved, so a generated profile lived only in
 *  React state and vanished on refresh/nav. Saves as a NEW version (never an
 *  overwrite). */
export async function saveSavedProfile(
  body: SaveProfileInput,
): Promise<{ saved: { id: string; createdAt: string } }> {
  return apiFetch<{ saved: { id: string; createdAt: string } }>('/presence/profiles', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** LANE JOB-TRUST G3 — one citation behind a scored criterion. Mirrors
 *  apps/web/src/lib/api.ts's PresenceOptimisationSource exactly (that file's
 *  L1462-1466) — the presentation view of a lake `LakeSource` (structurally
 *  `{ url, title?, retrievedAt? }`). `url` is the only required field. */
export interface PresenceOptimisationSource {
  url: string;
  title?: string;
  retrievedAt?: string;
}

/** LANE JOB-TRUST G3 — one scored criterion in the optimisation readout.
 *  Mirrors apps/web/src/lib/api.ts's PresenceOptimisationCriterion exactly
 *  (that file's L1473-1497), verified directly against PlatformOptimizerService.ts's
 *  own `ScoredCriterion` interface + its `plain_english`/`why_it_matters`/
 *  `fix_hint`/`last_verified_at`/`validation_status`/`display_role` field
 *  names (apps/api/services/PlatformOptimizerService.ts:63-96, 301-327) — the
 *  wire shape a typed `db.execute` + `c.json` response actually carries. */
export interface PresenceOptimisationCriterion {
  key: string;
  field?: string | null;
  label: string;
  /** The explicit, up-front explanation — shown WITHOUT a tap. */
  plain_english: string;
  /** The "machine behind the curtain" line — behind the more-info tap. */
  why_it_matters?: string | null;
  /** Whether the profile satisfies this criterion. */
  met: boolean;
  /** What to do when unmet (the live number already filled in by the scorer). */
  fix_hint?: string | null;
  importance?: 'high' | 'medium' | 'low';
  /** 0-100 source-strength benchmark shown to the user (Jason's certainty). */
  confidence?: number | null;
  /** The criterion's citations (full list, read behind the tap). */
  source?: PresenceOptimisationSource[];
  /** ISO timestamp the criterion was last verified. */
  last_verified_at?: string | null;
  validation_status?: 'validated' | 'flagged' | 'contested' | 'rejected';
  /** How the criterion participates in the readout (the 3-way hybrid):
   *  'scored' counts in the %, 'guaranteed' is shown as handled-for-you (the
   *  system enforces it), 'advisory' is guidance. Optional -> an older API
   *  body without it renders as 'scored' (back-compat). */
  display_role?: 'scored' | 'guaranteed' | 'advisory';
}

/** Mirrors apps/web/src/lib/api.ts's PresenceOptimisation (L1514-1533)
 *  verbatim. LANE JOB-TRUST G3 widens this from the prior trimmed
 *  score/band-only shape to the FULL per-criterion breakdown — the
 *  OptimisationReadout port (components/presence/OptimisationReadout.tsx)
 *  needs `criteria`/`validated_count`/`total_count`/`stuffing` to render the
 *  three honest states (unvalidated / partial / validated), not merely the
 *  headline number. `score` stays `null` (never a fabricated 0) when the
 *  platform's criteria are unvalidated — the SAME honest-absence rule
 *  SavedProfileSummary.score already documents for the saved-list path. */
export interface PresenceOptimisation {
  platform?: string;
  /** Which honest state this readout is in (drives the FE branch). */
  validation_status?: 'validated' | 'partial' | 'unvalidated';
  /** How many criteria were validated (the scored numerator's basis). */
  validated_count?: number;
  /** How many criteria exist for this platform (the honest denominator). */
  total_count?: number;
  /** 0..100 overall optimisation score, or null when unvalidated. */
  score?: number | null;
  /** Colour/word band of the score; null when there is no score. */
  band?: 'strong' | 'good' | 'needs_work' | 'thin' | null;
  /** Per-criterion met/unmet breakdown with served copy + citations. */
  criteria?: PresenceOptimisationCriterion[];
  /** The keyword-stuffing check: honest mirroring ranks, stuffing is
   *  penalised. `scored` is false when stuffing could not be assessed. */
  stuffing?: { detected: boolean; terms: string[]; scored: boolean };
}

/** Mirrors apps/web/src/lib/api.ts's PresenceGenerateResult (L1535-1552)
 *  verbatim, as served by POST /presence/generate (presence.routes.ts:1069-
 *  1090, verified directly against that handler's own `c.json({...})`
 *  literal — LOCKED, unedited by this lane). `blocked`/`lintGate` are
 *  ADDITIVE — present ONLY when the server's own render-blocking lint gate
 *  failed; their absence means a clean pass. This is the exact contract the
 *  R125 "silent nothing" bug class needs the caller to read honestly: a
 *  `blocked: true` response still carries `profile` (the draft that failed),
 *  so this client must render the REASON, never silently show nothing. */
export interface PresenceGenerateResult {
  profile: PresenceProfileResult;
  bridges: { yourPhrasing: string; standardTerm: string; evidenceRefId: string; claimLevel: string; confidence: number }[];
  flags: string[];
  usedDistilled: boolean;
  /** Server-computed optimisation score for the chosen platform (additive;
   *  absent on an older API build). Render-only — never trust a client value. */
  optimisation?: PresenceOptimisation;
  /** R125: the render-blocking lint gate FAILED — the profile above is still
   *  served in full; the FE must surface the reason instead of silently
   *  rendering past it (the exact regression class this lane's brief names). */
  blocked?: boolean;
  lintGate?: { location: string; kind: string; text: string };
  /** W23 — the presence_profiles row the SERVER banked before it ever
   *  answered (apps/api/routes/presence.routes.ts's composeAndBankPresence).
   *  Present means the work is already safe and this client must NOT save a
   *  second copy. null when the lint gate blocked the draft (not a keepable
   *  version) or the bank write itself failed — never a fabricated id. */
  saved?: { id: string; createdAt: string } | null;
}

// =============================================================================
// GET FOUND — LONG-OPERATION HONESTY (W23, docs/WALK-FINDINGS-2026-07-21.md;
// the owner's word `Go-longops`, 2026-07-24).
//
// HIS REPRO: "Couldn't generate right now: Load failed" — while the server's
// own `presence.linkedin` call COMPLETED at 67,257 ms (ai_call_log,
// error_message NULL, same minute as the banner). apiFetch above is a bare
// fetch with no AbortSignal, so the ceiling was mobile Safari's own (~60s)
// while the worker kept working. The API side now fast-acks and banks the
// finished profile server-side; this module loops the status GET internally so
// generatePresence() below keeps its EXACT signature and resolved shape —
// R387's pattern (pollWatchlistLoop, further down this file), copied.
// =============================================================================

/** POST /presence/generate's fast-ack half — returns the instant the server
 *  has SCHEDULED the run (one platform-gate read + one cron_runs insert). */
async function startPresenceGenerate(
  platform: string,
  targetRoleFamily: string[],
): Promise<{ ok: boolean; started: boolean; runId: string }> {
  return apiFetch<{ ok: boolean; started: boolean; runId: string }>('/presence/generate', {
    method: 'POST',
    body: JSON.stringify({ platform, targetRoleFamily }),
  });
}

/** GET /presence/generate-status/:runId — the status-poll half. `result` is
 *  null while the run is still 'running': the server never invents an
 *  in-progress profile, and never claims a stage it isn't tracking. */
export interface PresenceGenerateStatus {
  status: 'running' | 'ok' | 'error';
  result: PresenceGenerateResult | null;
}
export async function getPresenceGenerateStatus(runId: string): Promise<PresenceGenerateStatus> {
  return apiFetch<PresenceGenerateStatus>(
    `/presence/generate-status/${encodeURIComponent(runId)}`,
  );
}

/** Thrown when the poll ceiling below is reached while the server is STILL
 *  working. This is NOT a failure — it is the honest "not finished yet" state,
 *  and it carries the runId so the screen can resume against the same run
 *  (and so a remount after a backgrounded tab picks it straight back up).
 *  Distinct from a real error precisely so the UI can stop claiming failure
 *  over work that is still in flight — his exact W23 complaint. */
export class PresenceStillWorkingError extends Error {
  runId: string;
  constructor(runId: string) {
    super('still_working');
    this.name = 'PresenceStillWorkingError';
    this.runId = runId;
  }
}

/** Every 2s, up to ~3 minutes — bounded, never a silent infinite wait. The
 *  ceiling is set from the MEASURED worst case this route actually has, not a
 *  guess: a cold run is the bridge call (~43s measured) plus the compose call
 *  (~67,257 ms measured, his own W23 run) plus any regeneration pass the
 *  generate-to-criteria loop adds on top. Past it, the loop raises
 *  PresenceStillWorkingError rather than an error — because the server IS
 *  still working, and the profile will be banked when it lands. */
const PRESENCE_STATUS_INTERVAL_MS = 2000;
const PRESENCE_STATUS_MAX_ATTEMPTS = 90;

/** generatePresence()'s own loop, pulled out of the function body (the same
 *  "extract the testable core" discipline pollWatchlistLoop follows below —
 *  this repo's vitest run has no jsdom, so the state machine is proven
 *  directly with instantly-resolving fakes for start/getStatus/sleep, no
 *  timers, no wall-clock dependency).
 *
 *  `onRunStarted` lets the caller record the runId the moment it exists, so a
 *  navigation away mid-run can resume against it — see presenceRunStore.ts. */
export async function presenceGenerateLoop(
  start: () => Promise<{ runId: string }>,
  getStatus: (runId: string) => Promise<PresenceGenerateStatus>,
  sleep: (ms: number) => Promise<void>,
  onRunStarted?: (runId: string) => void,
): Promise<PresenceGenerateResult> {
  const { runId } = await start();
  onRunStarted?.(runId);
  return resumePresenceGenerateLoop(runId, getStatus, sleep);
}

/** The half of the loop that runs against an ALREADY-STARTED run — the resume
 *  path a remount takes with a runId recovered from the session store. Never
 *  starts a second generation: a run the user already paid for keeps running
 *  server-side whether or not this tab is watching. */
export async function resumePresenceGenerateLoop(
  runId: string,
  getStatus: (runId: string) => Promise<PresenceGenerateStatus>,
  sleep: (ms: number) => Promise<void>,
): Promise<PresenceGenerateResult> {
  for (let attempt = 0; attempt < PRESENCE_STATUS_MAX_ATTEMPTS; attempt++) {
    const check = await getStatus(runId);
    if (check.status === 'ok') {
      if (check.result) return check.result;
      break; // falls through to the honest "didn't come back" below
    }
    if (check.status === 'error') {
      throw new Error("That didn't finish — nothing was saved. Try again?");
    }
    await sleep(PRESENCE_STATUS_INTERVAL_MS);
  }
  throw new PresenceStillWorkingError(runId);
}

/**
 * G0 — generate platform profile copy, keyword-optimised for recruiter
 * search, true at every claim level. Ported verbatim from apps/web/src/lib/
 * api.ts's generatePresence (L1567-1575) — SAME POST /presence/generate
 * route, SAME {platform, targetRoleFamily} body, SAME resolved shape. Copy-
 * paste output only — this app never posts on the user's behalf (rule #1).
 *
 * W23 FIX: the ONE long synchronous request that used to back this (his
 * "Load failed" over a server call that had already succeeded) is now a
 * fast-ack plus the bounded status-poll loop above. Signature and resolved
 * shape are UNCHANGED for every caller — HOW this action completes is this
 * module's own business, the same Source-seam reasoning pollWatchlist()
 * already established.
 */
export async function generatePresence(
  platform: string,
  targetRoleFamily: string[],
  onRunStarted?: (runId: string) => void,
): Promise<PresenceGenerateResult> {
  return presenceGenerateLoop(
    () => startPresenceGenerate(platform, targetRoleFamily),
    getPresenceGenerateStatus,
    (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    onRunStarted,
  );
}

// =============================================================================
// CORPUS READINESS — the deterministic, code-computed "story told" score
// (apps/api/routes/corpus.routes.ts:3074 GET /corpus/readiness). Mirrors
// apps/web/src/lib/api.ts's CorpusReadiness/getCorpusReadiness exactly
// (L1704-1721). NEVER a model self-report — `score` is derived purely from
// corpus state (role coverage, evidence density, gold-moment count, whether a
// through-line + distilled profile exist), so a 0-corpus user gets an honest
// `score: 0` by construction, not a special-cased zero this client invents.
// This is the REAL signal C1's Home "story told %" reads — replacing
// placeholderHome.storyPct.
// =============================================================================

export interface CorpusReadiness {
  score: number;
  readyToTailor: boolean;
  parts: {
    coverage: number;
    evidence: number;
    /** Real count of gold-moment cards banked (capped display-side at 5 by
     *  the server's own `Math.min(1, goldCount / 5)` scoring, but this raw
     *  count field itself is uncapped) — the grounded stand-in for the
     *  placeholder's invented "threads found" figure. */
    gold: number;
    throughLine: boolean;
    distilled: boolean;
  };
  missing: string[];
}

/** GET /corpus/readiness — apps/api/routes/corpus.routes.ts:3074. requireAuth-
 *  gated, same Lucia cookie session every other route in this app uses. */
export async function getCorpusReadiness(): Promise<CorpusReadiness> {
  return apiFetch<CorpusReadiness>('/corpus/readiness');
}

// =============================================================================
// JOBS — the Discover/Jobs tab's inbox + search + poll-now (B-4/B-5). Every
// route below is EXISTING and untouched (this brief makes zero backend
// changes) — see apps/api/routes/jobs.routes.ts and apps/api/routes/
// watchlist.routes.ts for the handlers this simply calls.
//
// CASING WARNING (critical — read before touching a job field anywhere in
// this app): GET /jobs and POST /jobs/search both read via `db.execute(sql\`...\`)`
// — a RAW postgres.js query, not a typed Drizzle select. apps/api/lib/db.ts's
// createDb() passes no `transform: { column: { to: camelCase } }` option to
// postgres(), so a raw `j.*`/`s.*` row comes back in the DB's OWN column
// casing: snake_case (canonical_url, sponsorship_mentioned, work_mode,
// fit_score, sponsor_likelihood, radar_axes, fetched_at — see packages/
// schema/src/jobs.ts's column names). This is DIFFERENT from GET /jobs/:id,
// which uses a typed `db.select().from(jobs)` (Drizzle DOES camelCase a typed
// select) — so a single job fetched by id would come back camelCased, but
// that route is NOT called by this brief (list + search only; see the brief's
// own "COPY shapes verbatim" + apps/api/lib/asOf.ts's withAsOf doc comment,
// which states this explicitly: "this codebase's j.* raw-SQL reads are NOT
// camelCased before reaching the response"). apps/web/src/pages/Inbox.tsx's
// JobRow reads `job.canonicalUrl` on this SAME raw-row shape — that is a
// latent bug in that file (the field is actually `canonical_url` on the
// wire); JobRecord below is typed to what the row ACTUALLY carries, not to
// that existing (buggy) reference.
// =============================================================================

/** Verified directly against buildJobSearchQuery and the GET / handler's
 *  `SELECT j.*, s.fit_score, ...` (jobs.routes.ts:1427, 1563-1572).
 *  `asOf`/`staleMinutes` are the one exception: deriveAsOf/withAsOf
 *  (lib/asOf.ts) construct those as a plain object literal AFTER the raw
 *  query runs, so they ARE genuinely camelCase on the wire. */
/** LANE W2.1-BUCKETS — the country-agnostic six-bucket taxonomy (mirrors
 *  apps/api/lib/bucketJob.ts's JobBucket exactly; kept as a standalone,
 *  exported literal-union here since the throughline app does not depend on
 *  @careeropt/api). 'pass' is reachable ONLY via a user override
 *  (updateJobBucket) — the server's auto-scorer never produces it. */
export type JobBucketValue = 'home' | 'relocate' | 'bridge' | 'dream' | 'pass' | 'unsorted';

export interface JobRecord {
  id: string;
  user_id: string;
  canonical_url: string;
  source: string;
  company: string;
  title: string;
  location: string | null;
  work_mode: 'remote' | 'hybrid' | 'onsite' | null;
  employment_type: string | null;
  seniority: string | null;
  salary_min_aud: number | null;
  salary_max_aud: number | null;
  description: string | null;
  requirements: string[] | null;
  /** Boolean scan of the JD text for sponsorship language at extraction time.
   *  Null = never scanned / undetermined — never coerced to false. */
  sponsorship_mentioned: boolean | null;
  selection_criteria_mode: boolean;
  /** LANE W2.1-BUCKETS (extends W1-4): the six-bucket lane, or null for a job
   *  never scored under the bucket feature — read exactly like 'unsorted',
   *  never fabricated as a real value. */
  bucket: JobBucketValue | null;
  /** BOARD-TRUTH FIX (owner report: a multi-location "Nearby" card showing a
   *  foreign city as its face) — the specific `;`-delimited segment of
   *  `location` that justified a 'home'/'relocate' bucket (bucketJob.ts's
   *  BucketDecision.matchedLocation, apps/api/lib/bucketJob.ts). null for
   *  every other bucket and for a single-segment location — a card should
   *  prefer THIS over the raw `location` string when the bucket is 'home' or
   *  'relocate' and this is non-null, so the face of a "Nearby"/"Relocate"
   *  card is always the segment that actually justified the promise, never a
   *  foreign city that happened to lead the raw joined string. */
  resolved_location: string | null;
  created_at: string;
  updated_at: string;
  /** Numeric Postgres columns arrive as STRINGS over the wire (precision
   *  preservation) — parse with Number.parseFloat before use, exactly like
   *  apps/web/src/pages/Inbox.tsx's FitChip does for fit_score. Null when the
   *  job has never been scored under the current scenario (LEFT JOIN LATERAL
   *  with no match) — undetermined, never a fabricated 0. */
  fit_score: string | null;
  sponsor_likelihood: string | null;
  reasoning: string | null;
  gaps: string[] | null;
  strengths: string[] | null;
  radar_axes: Record<string, number> | null;
  scenario_id: string | null;
  /** W1-11(e) — ISO 8601, or null when freshness is genuinely unknown
   *  (a paste-import, a pre-fetchedAt-column row, a never-polled registry
   *  entry). Genuinely camelCase (see the module header). */
  asOf: string | null;
  staleMinutes: number | null;
  /** W21 (docs/WALK-FINDINGS-2026-07-21.md) — the honest staleness verdict:
   *  true once staleMinutes crosses the server's STALE_LISTING_THRESHOLD_
   *  MINUTES (apps/api/lib/asOf.ts), null alongside a null staleMinutes
   *  (genuinely unknown freshness is neither stale nor fresh). Served on
   *  GET /jobs, POST /jobs/search, and GET /jobs/:id — the SAME rows that
   *  already carry asOf/staleMinutes. jobBuckets.ts's staleListingNotice
   *  renders off this + asOf; never re-derived client-side. */
  isStale: boolean | null;
  /** POST /jobs/search only — ts_rank_cd relevance score (buildJobSearchQuery,
   *  jobs.routes.ts:1436). Absent on GET /jobs rows. */
  rank?: number;
  /** QA #34 — GET /jobs only (a LEFT JOIN LATERAL against `applications`,
   *  jobs.routes.ts's TRACKING_RANK). The most recent application row's
   *  status for THIS job, or null when the user has never tracked/applied to
   *  it — undetermined-by-absence, never a fabricated 'wishlist'. Drives the
   *  server-side sink-to-bottom sort AND the honest status chip JobCard
   *  renders. Absent (not merely null) on POST /jobs/search rows — that
   *  route was not extended in this landing (brief scope: GET /jobs only). */
  tracking_status?: 'wishlist' | 'applied' | 'screen' | 'onsite' | 'offer' | 'closed' | null;
  /** QA #34 — GET /jobs only. True when this job's created_at is strictly
   *  after the user's last recorded Jobs-list visit (attributes.jobsLastSeenAt,
   *  written by POST /jobs/mark-seen) — server-computed, drives the
   *  new-for-review badge + the float-to-top sort tier. A user who has never
   *  visited before reads every job as true (the honest/safe default — see
   *  jobs.routes.ts's readJobsLastSeenAt doc comment). Absent on POST
   *  /jobs/search rows (same scope note as tracking_status above). */
  new_since_last_visit?: boolean;
  /** AXIS-VIZ (R299 follow-up) — the list row's "glance chip": the single
   *  top-weighted axis key from this listing's STORED listing_axis_reads row
   *  (a cheap LEFT JOIN by canonical_url, jobs.routes.ts — the SAME identity
   *  the detail path's getOrDeriveListingAxisRead keys off), or absent when
   *  no read has ever been derived for this listing yet (the one-call-
   *  per-listing law lives entirely on the detail path — THIS list query
   *  never derives one). Present on BOTH GET /jobs and POST /jobs/search
   *  rows. JobCard's own contract: no field, no chip — never a placeholder
   *  guess rendered while a read doesn't exist. Humanize client-side the
   *  SAME way AxisLensCard already does (a display-only cosmetic step, never
   *  a re-derivation). */
  top_axis_key?: string;
  /** The paired register ('intern'|'early'|'mid'|'senior'|'exec') from the
   *  SAME stored row top_axis_key came from — always present together,
   *  always absent together (see topAxisGlance's own all-or-nothing
   *  contract, AxisLensService.ts). */
  axis_register?: string;
  /** CLASS 10 — THE RESCORING ENGINE (D21). GET /jobs only (withScoreConfidence,
   *  jobsLakeReads.ts). The honest tri-state widening jobMetrics.ts's
   *  fit-floor: 'scored' (a real fit_score number stands) / 'provisional'
   *  (some facets known, not enough to show a number — "I DO know, so far")
   *  / 'not_yet_attempted' (genuinely nothing yet). Absent on POST
   *  /jobs/search rows this pass (GET /jobs only, mirrors tracking_status'
   *  own scope note). */
  score_confidence?: 'scored' | 'provisional' | 'not_yet_attempted';
  /** The named facets that DID contribute (jd_title_read / jd_comp_band /
   *  jd_location_read / corpus_depth / declared_home_base /
   *  declared_direction) — structured, countable, never a generated
   *  sentence. Present together with score_pending_facets. */
  score_known_facets?: string[];
  /** The named facets still missing — the honest chip's "pending" half. */
  score_pending_facets?: string[];
}

export type JobSort = 'fit' | 'recent' | 'bucket';

/** D19 — THE DECLARED-INPUTS LAW. GET /jobs's own served banner + criteria-
 *  transparency line, carried alongside the jobs array so the FE never
 *  re-derives "what's missing" client-side (no-frontend-bandaids law).
 *  missingInputs is empty when the geography gate is satisfied (either
 *  home_country OR home_postal_code declared — see DeclaredAnswersService's
 *  REQUIRED_FOR_SEARCH doc comment for why an OR, not an AND, gate). */
export interface DeclaredInputsSummary {
  missingInputs: string[];
  sortCriteria: string;
}

/** GET /jobs?sort=... — apps/api/routes/jobs.routes.ts. Returns every job for
 *  the user, each joined with its latest score under the resolved scenario
 *  (explicit ?scenario= or the user's primary). sort='bucket' groups
 *  home→relocate→bridge→dream→unsorted→pass (the BUCKET_RANK precedence,
 *  pass sunk last), fit DESC within each group — backend-driven ordering;
 *  this function never re-sorts. */
export async function getJobs(
  sort: JobSort = 'fit',
): Promise<{ jobs: JobRecord[]; scenarioId: string | null; declaredInputs?: DeclaredInputsSummary }> {
  const qs = new URLSearchParams({ sort });
  return apiFetch<{ jobs: JobRecord[]; scenarioId: string | null; declaredInputs?: DeclaredInputsSummary }>(
    `/jobs?${qs.toString()}`,
  );
}

/** D19/D17 — THE DECLARED-INPUTS ASK. POST /jobs/declare-answer: persists ONE
 *  declared answer (questionKey/answer), which server-side wakes the per-user
 *  bucket resweep (home_country/home_postal_code only) + the keystone-hub
 *  coverage write (every key) — see DeclaredAnswersService.declareAnswer's
 *  own doc comment for the full wake contract. This function is a thin
 *  caller; it does not itself refresh the jobs list — the caller (Jobs.tsx)
 *  re-fetches via useJobsInbox's refresh() after a successful declare. */
export async function declareAnswer(
  questionKey: string,
  answer: string,
): Promise<{ ok: true; questionKey: string; answer: string }> {
  return apiFetch<{ ok: true; questionKey: string; answer: string }>('/jobs/declare-answer', {
    method: 'POST',
    body: JSON.stringify({ questionKey, answer }),
  });
}

/** LANE JOB-TRUST — one row of GET /jobs/:id's `scores[]` (jobs.routes.ts:2140,
 *  the `scores` map literal built from `latestByScenario`): the LATEST score
 *  per search scenario for this job, so the detail page can show "AU 62 ·
 *  Stateside 78" and let the user read every lens they've been scored under,
 *  not just the primary one. Built from a TYPED `db.select().from(jobScores)`
 *  (packages/schema/src/jobs.ts's jobScores table) — Drizzle camelCases a
 *  typed select, so this genuinely arrives camelCase on the wire (unlike
 *  JobRecord's raw-SQL snake_case above; see that type's own casing-warning
 *  header). `fitScore`/`radarAxes` are the route's OWN floored values (P4/D-5
 *  no-invention fit floor: undetermined -> null, never a fabricated 0/zeroed
 *  axes) — never the bare stored column. */
export interface JobScoreRecord {
  id: string;
  jobId: string;
  scenarioId: string | null;
  rubricVersion: string;
  /** Floored by the route's own corpus-signal check: null when the corpus is
   *  too thin to stand behind a number — never a fabricated 0. */
  fitScore: number | null;
  sponsorLikelihood: string | null;
  reasoning: string | null;
  gaps: string[] | null;
  strengths: string[] | null;
  /** The 6-axis radar, floored the SAME way as fitScore — null when
   *  undetermined. Values as served; this app never invents an axis the
   *  server didn't return. */
  radarAxes: Record<string, number> | null;
  /** WHY fitScore/radarAxes are null-or-not, named explicitly so the FE can
   *  render the honest reason rather than guessing from a bare null. */
  fitDetermination: 'undetermined' | 'unscored' | 'scored';
  /** Human label for this scenario ("AU" / "Stateside" / "Legacy
   *  (pre-scenario)") — resolved server-side from search_scenarios.name, or
   *  the fixed legacy label for a pre-scenario score. Never invented here. */
  scenarioName: string;
  userJobId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One axis's weight in a listing's axis-lens read — AXIS-P1
 *  (AxisLensService.ts's ListingAxisWeight, served verbatim). `matchedCues`
 *  is the traceability record (NO INVENTION): the literal JD text this
 *  weight was actually traced to — never rendered as proof text on the FE in
 *  P1 (the card shows the weight bars only), but typed here since it is part
 *  of the served shape and a future surface may want it. */
export interface AxisLensWeight {
  axisKey: string;
  weight: number;
  matchedCues: string[];
}

/** The axis-lens "how this role reads" read for one posting — AXIS-P1
 *  (apps/api/services/AxisLensService.ts's AxisLensRead, served verbatim).
 *  DIFFERENT CONCEPT from JobScoreRecord.radarAxes (a candidate-FIT rubric) —
 *  never conflated in copy or code; see AxisLensService.ts's own header. */
export interface AxisLensReadRecord {
  weights: AxisLensWeight[];
  /** 'intern' | 'early' | 'mid' | 'senior' | 'exec' — the posting's own
   *  seniority reading, distinct from any cover-letter tone register. */
  register: string;
  /** 0..1 — how much of the JD's own requirement text got claimed by axis
   *  cues. Honest, deterministic; never the model's self-report. */
  coverage: number;
  /** 'reads_unusual' | 'jd_too_thin' | null — set only when the read is
   *  honestly thin/unusual; null on a normal, confidently-covered read. */
  coverageNote: string | null;
  taxonomyVersion: number;
}

/** One real corpus achievement that grounds an axis's evidence score — the
 *  NO-INVENTION receipt (EvidenceScoringService.EvidenceReceipt, served
 *  verbatim). `matchedText` is the achievement's own headline snippet;
 *  `matchedTerms` the exact shared tokens the lexical match found;
 *  `affinityScore` the achievement's persisted axis affinity (null when only the
 *  lexical signal fired). At least one receipt is required for evidence > 0. */
export interface EvidenceReceiptRecord {
  achievementId: string;
  matchedText: string;
  matchedTerms: string[];
  affinityScore: number | null;
}

/** One served axis's grounded evidence read — the USER half of JobFitRadar
 *  (FIX-E part 2; EvidenceScoringService.AxisEvidenceScore, served verbatim).
 *  `evidence` is 0..1; the server's grounding gate guarantees `evidence > 0`
 *  implies `receipts.length >= 1`. An axis scored with zero receipts serves
 *  `evidence: 0` + `receipts: []` — the honest "no evidence yet", distinct from
 *  an axis that was never scored ("not scored yet", which is a null userScores or
 *  an axis absent from the array). Never fabricated client-side. */
export interface AxisEvidenceScoreRecord {
  axisKey: string;
  evidence: number;
  receipts: EvidenceReceiptRecord[];
}

/** GET /jobs/:id (jobs.routes.ts:2140) — the resolved single-job loader
 *  (loadJobForUser: lake rows resolve through COALESCE(canonical_listing_id,
 *  id), legacy rows serve directly). WIRE-AUDIT fix #3: JobDetail previously
 *  resolved ONLY from the in-memory inbox list, so a deep link / refresh /
 *  share rendered a false "Job not found" for a real job. This is the same
 *  row shape as GET /jobs (incl. reasoning/strengths/gaps/radar_axes — the
 *  fit-reasoning payload), plus the per-scenario score rows the detail read
 *  serves. LANE JOB-TRUST widens `scores`/`score` from `unknown` to the real
 *  served shape (JobScoreRecord) — the transparency surface (radar axes,
 *  per-scenario chips) needs these typed, not merely fetched-and-ignored. A
 *  real 404 (not_found) still surfaces as an ApiError — the honest missing
 *  case, distinct from "not loaded yet".
 *
 *  AXIS-P1: `axisRead` is the lens's "how this role reads" read — null when
 *  no ANTHROPIC_API_KEY is configured (the route degrades gracefully, never
 *  a 503) or, transiently, on the very first request for a brand-new
 *  posting's derivation (never re-null afterward — the store-once seam
 *  persists it for every future viewer). Served, never invented client-side.
 *
 *  W19 (guided-walk 2026-07-21/22): `relocationScore` is the SAME honest
 *  decomposition the relocation toggle's POST response carries (see
 *  RelocationScoreSnapshot below) — read-only, computed fresh on every GET,
 *  never a write. Its `copy` is what JobDetail renders in the toggle panel
 *  BEFORE the user ever touches the switch, so "location isn't a factor
 *  here — this role's location isn't resolved to a country yet" (or the
 *  home-country-unknown twin) is visible on first load, not only after a
 *  flip. Always present once the job itself resolved (never null on a
 *  successful response) — baseScore/effectiveScore are null only when the
 *  job has no stored score yet, same honesty as the toggle route. */
export async function getJob(id: string): Promise<{
  job: JobRecord;
  scores: JobScoreRecord[];
  /** Back-compat: the primary/first scenario's score, or null when the job
   *  has never been scored under any scenario. */
  score: JobScoreRecord | null;
  asOf: string | null;
  staleMinutes: number | null;
  /** W21 — see JobRecord's own isStale doc comment; the SAME honest verdict,
   *  served as a top-level sibling here (this endpoint's `job` is a
   *  ResolvedJob, not a flat JobRecord list row, so asOf/staleMinutes/
   *  isStale ride alongside it rather than inside it). */
  isStale: boolean | null;
  axisRead: AxisLensReadRecord | null;
  /** FIX-E part 2 (Lane L-H) — the per-axis grounded evidence read (the radar's
   *  Layer-2 gold polygon + per-axis rows). `null` = "not scored yet" (the
   *  scorer didn't run: no ANTHROPIC_API_KEY, a cold/undetermined corpus, or a
   *  fail-open degrade). A present array carries one entry per served axis;
   *  each score > 0 is receipt-backed (NO INVENTION, server-gated). Served,
   *  never invented client-side. */
  userScores: AxisEvidenceScoreRecord[] | null;
  /** W19 (Lane L-I) — the read-only relocation decomposition, served on the
   *  INITIAL load so the honest reason (incl. 'job_country_unknown') renders
   *  before any toggle flip. */
  relocationScore: RelocationScoreSnapshot;
}> {
  return apiFetch(`/jobs/${encodeURIComponent(id)}`);
}

/** QA #34 — POST /jobs/mark-seen (apps/api/routes/jobs.routes.ts). Stamps the
 *  server's `attributes.jobsLastSeenAt` to now; called once per Jobs-list
 *  visit (useJobsInbox's mount effect), never per-poll and never inside
 *  getJobs itself — see the route's own doc comment for why inlining it
 *  there would collapse "what's new" to always-empty. Returns the server's
 *  own resolved timestamp — a display convenience only, not currently
 *  rendered anywhere (the badge itself derives from job.new_since_last_visit,
 *  already computed server-side on the NEXT getJobs call). */
export async function markJobsSeen(): Promise<{ ok: boolean; jobsLastSeenAt: string }> {
  return apiFetch<{ ok: boolean; jobsLastSeenAt: string }>('/jobs/mark-seen', { method: 'POST' });
}

/**
 * LANE W2.1-BUCKETS — PATCH /jobs/:id/bucket (apps/api/routes/jobs.routes.ts).
 * The user-sort half of the taxonomy: sets the job's bucket to EXACTLY the
 * value picked (including 'pass', which the server's auto-scorer can never
 * itself produce) and marks it TERMINAL server-side (bucket_source='user') —
 * a future re-score will never silently revert this choice. Works for both a
 * legacy `jobs` row and a lake-origin job (the route tries both tables; see
 * its own doc comment). Thin: this function is the entire client-side
 * surface — the write + the terminal marking both happen server-side.
 */
export async function updateJobBucket(
  jobId: string,
  bucket: JobBucketValue,
): Promise<{ ok: boolean; id: string; bucket: JobBucketValue; bucketSource: 'user' }> {
  return apiFetch(`/jobs/${jobId}/bucket`, {
    method: 'PATCH',
    body: JSON.stringify({ bucket }),
  });
}

/** Mirrors the SearchInput zod schema exactly (jobs.routes.ts's own SearchInput). */
export interface JobSearchFilters {
  bucket?: JobBucketValue;
  source?: string;
  company?: string;
  sponsorshipMentioned?: boolean;
}

export interface JobSearchRequest {
  query: string;
  filters?: JobSearchFilters;
  /** Runs a fresh watchlist poll sweep before searching (available on this
   *  route per its own zod schema) — NOT used by the Jobs tab's dedicated
   *  "poll now" button, which calls pollWatchlist()/POST /watchlist/poll
   *  directly per the brief. Left available here for a future "search, and
   *  check for anything new first" affordance. Defaults false (instant search). */
  sweepFirst?: boolean;
  scenario?: string;
  limit?: number;
}

/** POST /jobs/search — apps/api/routes/jobs.routes.ts:1456. Full-text search
 *  (plainto_tsquery, ts_rank_cd) across every stored posting for the user,
 *  scored + bucketed through the SAME pipeline GET /jobs uses (never
 *  recomputed here). `sweep` is the honest poll summary when sweepFirst was
 *  requested — null otherwise, never a synthesized "0 found." */
export async function searchJobs(
  input: JobSearchRequest,
): Promise<{ jobs: JobRecord[]; scenarioId: string | null; query: string; sweep: PollSummary | null }> {
  return apiFetch('/jobs/search', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Mirrors apps/api/lib/discovery.ts's PollSummary (L217-243) exactly — the
 *  honest, count-only shape POST /watchlist/poll and the sweepFirst branch of
 *  POST /jobs/search both return. Every count here is a REAL tally the server
 *  computed this pass; the brief's "honest progress (the served summary, no
 *  invented counts)" is enforced by rendering exactly these fields and
 *  nothing else. */
export interface PollSummary {
  sourcesPolled: number;
  candidates: number;
  ingestedNew: number;
  scored: number;
  skipped: number;
  skips: { url: string; source: string }[];
  perSource: Record<string, { rawCount: number; candidates: number; error: string | null }>;
  errors: { url: string; message: string }[];
  autoDiscovered: {
    company: string;
    kind: string;
    boardSlug: string;
    enabled: boolean;
    registryId: string | null;
  }[];
}

/** POST /watchlist/poll's fast-ack half — W11 FIX (docs/WALK-FINDINGS-
 *  2026-07-21.md row W11; apps/api/routes/watchlist.routes.ts's own doc
 *  comment on this route has the full diagnosis). Returns the instant the
 *  server has SCHEDULED the sweep (one DB insert) — never waits for it. */
async function startWatchlistPoll(): Promise<{ ok: boolean; started: boolean; runId: string }> {
  return apiFetch<{ ok: boolean; started: boolean; runId: string }>('/watchlist/poll', {
    method: 'POST',
  });
}

/** GET /watchlist/poll-status/:runId — the status-poll half pollWatchlist()
 *  loops against below. `summary` is null while the sweep is still
 *  'running' (the server never invents an in-progress summary) — the SAME
 *  honest PollSummary shape once 'ok'/'partial'/'error'. */
export interface WatchlistPollStatus {
  status: 'running' | 'ok' | 'partial' | 'error';
  summary: PollSummary | null;
}
async function getWatchlistPollStatus(runId: string): Promise<WatchlistPollStatus> {
  return apiFetch<WatchlistPollStatus>(`/watchlist/poll-status/${encodeURIComponent(runId)}`);
}

/** Every 1.5s, up to ~60s total — a short, bounded status-poll cadence, never
 *  a silent infinite wait. An honest give-up past this ceiling (a real
 *  server-side stall, or a run so large even the BACKGROUND sweep hasn't
 *  finished) surfaces as a real error, same as any other failure — never a
 *  fabricated success. */
const POLL_STATUS_INTERVAL_MS = 1500;
const POLL_STATUS_MAX_ATTEMPTS = 40;

/** pollWatchlist()'s own loop, pulled out of the function body (the SAME
 *  "extract the testable core" discipline useAuth.ts's requestSignOut/
 *  SIGNED_OUT_STATE doc comment names — this repo's vitest run has no jsdom,
 *  so a hook/async-loop is proven directly rather than through a rendered
 *  component) so a test can drive the state machine with instantly-resolving
 *  fakes for `start`/`getStatus`/`sleep` — no real or faked timers, no wall-
 *  clock-dependent test. pollWatchlist() below is the only real caller,
 *  wiring the genuine network calls + a real setTimeout-based sleep. */
export async function pollWatchlistLoop(
  start: () => Promise<{ runId: string }>,
  getStatus: (runId: string) => Promise<WatchlistPollStatus>,
  sleep: (ms: number) => Promise<void>,
): Promise<{ ok: boolean } & PollSummary> {
  const { runId } = await start();
  for (let attempt = 0; attempt < POLL_STATUS_MAX_ATTEMPTS; attempt++) {
    const check = await getStatus(runId);
    if (check.status === 'ok' || check.status === 'partial') {
      if (check.summary) return { ok: true, ...check.summary };
      break; // falls through to the honest "didn't come back" error below
    }
    if (check.status === 'error') {
      throw new Error("Checking your sources failed partway through — try again.");
    }
    await sleep(POLL_STATUS_INTERVAL_MS);
  }
  throw new Error('Still checking your sources — try again in a moment.');
}

/** POST /watchlist/poll — apps/api/routes/watchlist.routes.ts:220. Polls every
 *  enabled watchlist source for new postings (dedupe, extract, score) and
 *  resolves with the SAME PollSummary shape sweepFirst produces. This is the
 *  brief's dedicated "poll now" button target when the user wants to refresh
 *  the inbox WITHOUT also running a search.
 *
 *  W11 FIX (docs/WALK-FINDINGS-2026-07-21.md row W11): UNCHANGED signature
 *  and resolved shape for every existing caller (useWelcomeBackPoll.ts's
 *  Home "Check now", useJobsInbox.ts's Jobs "Check for new jobs") — the ONE
 *  big synchronous request that used to back this (Safari "Load failed" on
 *  mobile, the sweep fanning out inline past the fetch layer's own timeout)
 *  is now a fast-ack plus this short client-side status-poll loop. Neither
 *  hook needed to change — the Source seam means HOW this action completes
 *  is this module's own business. */
export async function pollWatchlist(): Promise<{ ok: boolean } & PollSummary> {
  return pollWatchlistLoop(
    startWatchlistPoll,
    getWatchlistPollStatus,
    (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  );
}

// =============================================================================
// SOURCE GUT-CHECK — D18's periodic micro-loop (docs/design/fix-architecture-
// 2026-07-08/{FIX-ARCHITECTURE.md,BUILD-PLAN.md} Class 8). "Still want me
// pulling every $SOURCE job?" — on-visit, not a background cron: the caller
// fetches this once when the watchlist screen mounts.
// =============================================================================

export type WatchlistGutCheckAnswer = 'keep' | 'adjusted' | 'stopped';

export interface WatchlistGutCheckCandidate {
  sourceId: string;
  label: string;
  totalJobsFound: number;
  daysSinceLastCheck: number | null;
}

/** GET /watchlist/gut-check — every standing source due for a "still want
 *  this?" re-confirmation (30+ days since created/last-answered). Empty when
 *  nothing is due — a real, honest, common state, not an error. */
export async function getWatchlistGutCheckCandidates(): Promise<{
  candidates: WatchlistGutCheckCandidate[];
}> {
  return apiFetch('/watchlist/gut-check');
}

/** POST /watchlist/gut-check/:id/answer — the person's keep/adjusted/stopped
 *  verdict on ONE source. 'stopped' disables it server-side; every answer
 *  resets the 30-day clock. Only the fields the gut-check card itself might
 *  need back are typed here (id/enabled) — the route echoes the full
 *  watchlist_sources row, this client only promises what callers use. */
export async function answerWatchlistGutCheck(
  sourceId: string,
  answer: WatchlistGutCheckAnswer,
): Promise<{ ok: true; source: { id: string; enabled: boolean } }> {
  return apiFetch(`/watchlist/gut-check/${encodeURIComponent(sourceId)}/answer`, {
    method: 'POST',
    body: JSON.stringify({ answer }),
  });
}

/** Mirrors apps/api/lib/atsAutoDiscovery.ts's AtsTokenMatch exactly — one
 *  detected ATS token (Greenhouse/Lever/Ashby/SmartRecruiters/Workday) found
 *  either in the careers URL's own path or by scanning its page for a
 *  vendor-hosted link. Empty on the response's `matches` array is the honest
 *  "not an ATS-fronted employer" read — never treated as an error by this
 *  client (see DiscoverWatchlistResult's doc comment). */
export interface AtsTokenMatch {
  kind: string;
  boardSlug: string;
  sourceUrl: string;
  workday?: { tenant: string; site: string; wdNum: string };
}

/** Mirrors apps/api/lib/atsAutoDiscovery.ts's ResolveNoopReason exactly — why
 *  a call came back with an empty matches/created (never populated alongside
 *  a genuinely non-empty result). Additive field (2026-07-04 bug-fix lane):
 *  every no-op outcome now carries WHY, instead of a bare, unexplained empty
 *  result — the exact prod defect (POST /watchlist/discover returning
 *  {matches:[], created:[]} with nothing to tell the user) this fixes. */
export type DiscoverNoopReason =
  | 'invalid_careers_url'
  | 'domain_claimed_today'
  | 'fetch_failed'
  | 'no_ats_detected'
  | 'already_watched'
  | 'already_ats_hosted_url';

/** Mirrors apps/api/lib/atsAutoDiscovery.ts's ResolveResult exactly, as
 *  served by POST /watchlist/discover's `{ok, ...result}` spread
 *  (watchlist.routes.ts:165, `ok: result.error === null`). `created` is only
 *  ever non-empty for a pollable ATS kind this user didn't already subscribe
 *  to (the same-user-dedup rule the route's own file header documents) —
 *  matches-with-zero-created is a normal, honest "already watching this one"
 *  outcome, not a failure. `reason` (additive, 2026-07-04) explains any
 *  empty outcome; `null` whenever the call was NOT a no-op. */
export interface DiscoverWatchlistResult {
  ok: boolean;
  matches: AtsTokenMatch[];
  created: { id: string; kind: string; boardSlug: string; enabled: boolean; registryId: string | null }[];
  error: string | null;
  reason?: DiscoverNoopReason | null;
}

/** POST /watchlist/discover {careersUrl} — watchlist.routes.ts:156
 *  (W1-10a/c). Hand-feeds a company's OWN careers page URL (never a protected
 *  board — resolveAtsForCompany enforces the same PASTE_REQUIRED_HOSTS split
 *  every ingest path uses) for immediate ATS-token detection, rather than
 *  waiting for a job posting from that employer to surface it automatically.
 *  Idempotent per domain-per-day at the SERVER (once-per-domain claim via KV)
 *  — calling this twice for the same URL the same day is a safe, cheap no-op,
 *  never a duplicate side effect. */
export async function discoverWatchlistSource(careersUrl: string): Promise<DiscoverWatchlistResult> {
  return apiFetch<DiscoverWatchlistResult>('/watchlist/discover', {
    method: 'POST',
    body: JSON.stringify({ careersUrl }),
  });
}

/** Mirrors apps/api/lib/watchlistDerive.ts's DistillResult exactly — one
 *  declared Get Found title distilled into grounded, board-searchable
 *  keywords. An all-ungrounded model response yields an empty `keywords`
 *  array (the code-level validateGroundedKeyword gate, never a thrown error
 *  for one bad title) — this client renders that as "no searchable terms
 *  found for that title," never a fabricated keyword. */
export interface WatchlistDistillResult {
  sourceTitle: string;
  keywords: string[];
}

/** Mirrors apps/api/lib/watchlistDerive.ts's DeriveSummary exactly, as served
 *  by POST /watchlist/rederive's `{ok, ...summary, enabledSearchCount}` spread
 *  (watchlist.routes.ts). Every count here is the server's OWN tally of
 *  what it actually did this pass (created/reactivated/disabledStale rows,
 *  keywords the hands-on-coding filter dropped) — this client renders exactly
 *  these fields, the same "no invented counts" discipline PollSummary's own
 *  doc comment states above.
 *
 *  QA Tier-2 #5 (geography, deployed lane B2): `countries` is the SET this
 *  pass fanned every keyword to — derived from the user's OWN active
 *  search_scenarios, never a hardcoded ['us','au'] constant. `blockedReason`
 *  is present ONLY when zero countries resolved (no active scenario carries a
 *  resolvable geography) — the HONEST BLOCK: the pass created nothing at all
 *  rather than guessing a country, and this field is the served reason to
 *  show the user, never synthesized client-side.
 *
 *  QA #15 (Wave 2, lane B — docs/DEMO-WALKTHROUGH-issues.md row #15):
 *  `enabledSearchCount` (additive) is the CURRENT total of this user's own
 *  enabled watchlist_sources rows (every kind, hand-made + auto-derived)
 *  AFTER this pass ran — the one number that answers "is my watchlist okay
 *  now," which the raw created/reactivated counts alone never did (they
 *  read as noise without a running total to anchor them, per Jason's own
 *  "I have no idea what that means" live reaction). */
export interface WatchlistDeriveSummary {
  desiredRoles: string[];
  excludesHandsOnCoding: boolean;
  distilled: WatchlistDistillResult[];
  excludedHandsOnCoding: string[];
  created: { what: string; country: string }[];
  reactivated: { what: string; country: string }[];
  disabledStale: { what: string; country: string }[];
  untouchedManual: number;
  /** The geographies this pass fanned every keyword to (lowercased ISO-3166),
   *  read from the user's OWN active search_scenarios — empty exactly when
   *  `blockedReason` is set. */
  countries: string[];
  /** Present ONLY when the pass created nothing because no geography could
   *  be resolved (zero active search scenarios) — render this verbatim as
   *  the honest next-step message; never fall back to a fabricated count. */
  blockedReason?: string;
  /** Legacy auto-derived rows (predate metadata.sourceTitle) this pass
   *  matched to a declared title and backfilled — visible, not silent. */
  backfilledSourceTitle: { id: string; what: string; country: string; sourceTitle: string }[];
  /** QA #15 — additive. The user's CURRENT total enabled watchlist_sources
   *  rows (every kind) after this pass. Always a real served count; never
   *  computed client-side from the other fields on this summary. */
  enabledSearchCount: number;
}

/** POST /watchlist/rederive — watchlist.routes.ts (W1-12). Re-derives the
 *  user's adzuna watchlist rows from their Get Found declared titles
 *  (user_profiles.attributes.desiredRoles), fanned to the user's OWN declared
 *  search-scenario geographies (QA Tier-2 #5 — never a hardcoded country
 *  list) — the manual trigger for "these need to feed dynamically from the
 *  get found area" (his directive, 2026-07-02). A user with no declared
 *  titles yet gets back an honest `desiredRoles: []` and zero
 *  created/reactivated rows, never an invented seed list. 503
 *  `no_anthropic_key` propagates as an ApiError, same as
 *  pollWatchlist/searchJobs's sweepFirst path. */
export async function rederiveWatchlist(): Promise<{ ok: boolean } & WatchlistDeriveSummary> {
  return apiFetch<{ ok: boolean } & WatchlistDeriveSummary>('/watchlist/rederive', { method: 'POST' });
}

// =============================================================================
// SETTINGS/ACCOUNT — the Settings tab's billing/voice/account surface. NO
// backend changes: every function below calls an EXISTING, already-shipped
// route (apps/api/routes/billing.routes.ts, voice.routes.ts, account.routes.ts)
// and every field is typed to exactly what that route already returns —
// verified by reading each handler directly, cited per group below.
// =============================================================================

/** Mirrors apps/api/lib/entitlements.ts's ResolvedEntitlement exactly
 *  (EntitlementTier = 'free'|'trial'|'paid', Capability is a closed string
 *  union the FE never needs to enumerate itself — it only ever checks
 *  `.includes(x)` against whatever the server sent, so a FUTURE capability
 *  the server adds renders correctly with zero FE change). `reason` mirrors
 *  ResolvedEntitlement's own machine-readable reason field verbatim — the
 *  brief's "no-frontend-bandaids: the backend explains itself, the FE just
 *  renders the explanation" is honored by treating this as an opaque label,
 *  never re-deriving a reason client-side. */
export interface Entitlement {
  tier: 'free' | 'trial' | 'paid';
  plan: SubscriptionPlanName | null;
  capabilities: string[];
  reason:
    | 'no_subscription'
    | 'trialing'
    | 'trial_expired'
    | 'active'
    | 'past_due_in_grace'
    | 'past_due_lapsed'
    | 'canceled';
}

/** billing.routes.ts's SubscriptionPlan re-typed locally (not imported from
 *  @careeropt/schema here) ONLY because DISPLAY_PRICE_TABLE in priceTable.ts
 *  already imports the real @careeropt/schema type — this alias just gives
 *  the settings-specific interfaces below a short, local name; the two are
 *  structurally identical (kept in sync by priceTable.test.ts's literal
 *  mirror check, which would fail the moment the two drifted). */
export type SubscriptionPlanName = 'weekly' | 'monthly' | 'annual' | 'founder';

/** GET /api/billing/status's `subscription` field — billing.routes.ts:114-122
 *  (the exact object literal returned when a row exists). Null when the user
 *  has never subscribed (no row at all — NOT the same as a lapsed/canceled
 *  row, which still has fields, just an entitlement.tier of 'free'). */
export interface SubscriptionStatusRow {
  plan: SubscriptionPlanName | null;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  founderSlot: number | null;
}

/** GET /api/billing/status's full response shape — billing.routes.ts:80-124.
 *  `{configured:false}` is the ENTIRE response (no entitlement/subscription
 *  keys at all) when STRIPE_SECRET_KEY is unset — the pre-Stripe-keys reality
 *  this codebase's own module header calls "TEST-MODE-READY, not live." The
 *  settings screen renders that as an honest "billing not yet enabled" state,
 *  never a spinner stuck forever or a fabricated free-tier badge. */
export type BillingStatus =
  | { configured: false }
  | { configured: true; entitlement: Entitlement; subscription: SubscriptionStatusRow | null };

/** GET /api/billing/status — billing.routes.ts:81. requireAuth-gated (the
 *  same Lucia cookie session every other route in this app relies on). */
export async function getBillingStatus(): Promise<BillingStatus> {
  return apiFetch<BillingStatus>('/billing/status');
}

/**
 * Mirrors billing.routes.ts's GET /api/billing/entitlement response exactly
 * (billing.routes.ts:157-170, G1/R184/R185 — verified directly against that
 * handler's own `c.json({...})` literal, not re-derived from Entitlement
 * above, which is /status's DIFFERENT, grants-blind field). `reason` adds
 * exactly one value beyond Entitlement's own union: 'promo_grant' — served
 * ONLY when an active entitlement_grants row (founder/beta-tester/promo)
 * won BEST-of over whatever the subscription alone would have resolved to
 * (see apps/api/lib/entitlements.ts's ResolvedEntitlement.reason doc
 * comment). `grantLabel`/`endsAt` are OPTIONAL — present ONLY alongside
 * reason==='promo_grant' (the route omits them entirely otherwise, per its
 * own comment: "never a null placeholder on every other response") — #45's
 * "founder/trial grant label + ends-at when present" reads exactly these two
 * fields, never inventing a label when the server sent none.
 */
export interface BillingEntitlement {
  tier: 'free' | 'trial' | 'paid';
  reason:
    | 'no_subscription'
    | 'trialing'
    | 'trial_expired'
    | 'active'
    | 'past_due_in_grace'
    | 'past_due_lapsed'
    | 'canceled'
    | 'promo_grant';
  capabilities: string[];
  /** The active grant's own free-text label ('founder' | 'beta-tester' |
   *  'launch-promo-14d' | ...), rendered verbatim — never present unless
   *  reason==='promo_grant'. */
  grantLabel?: string;
  /** ISO 8601 — the SAME grant's endsAt, present only alongside grantLabel. */
  endsAt?: string;
}

/** GET /api/billing/entitlement — billing.routes.ts:157. requireAuth-gated.
 *  Deliberately configured-agnostic (unlike getBillingStatus, which
 *  short-circuits to {configured:false} when Stripe is unset): a promo grant
 *  is orthogonal to Stripe, so this route always serves a live resolution —
 *  there is no {configured:false} branch to handle here at all (see the
 *  route's own module-header comment, verified directly). */
export async function getBillingEntitlement(): Promise<BillingEntitlement> {
  return apiFetch<BillingEntitlement>('/billing/entitlement');
}

// =============================================================================
// PROMO CODE REDEMPTION (PROMO-TOKENS lane) — POST /api/promo/redeem
// (promo.routes.ts). A successful redemption mints a REAL entitlement_grants
// row via the SAME mechanism GET /api/billing/entitlement's own grantLabel/
// endsAt fields already surface — so after a successful redeemPromoCode call,
// the caller should re-run getBillingEntitlement (useSettings.ts's own
// refresh()) to pick up the new grant, rather than this function inventing an
// entitlement shape of its own to hand back.
// =============================================================================

/** Mirrors promo.routes.ts POST /redeem's response shape exactly. A
 *  successful redemption serves `redeemed: true` plus the exact grant that
 *  was minted (label/startsAt/endsAt, the SAME fields entitlement_grants
 *  itself carries) and an honest, ready-to-render Mira-voice `message` — the
 *  client never re-derives its own success copy from the raw fields. */
export interface PromoRedeemResult {
  redeemed: true;
  grant: { label: string; startsAt: string; endsAt: string };
  message: string;
}

/** Every DISTINCT failure shape promo.routes.ts POST /redeem can serve
 *  (invalid_code / expired_code / exhausted_code / already_redeemed) arrives
 *  as a non-2xx response, so apiFetch throws an ApiError for each — the
 *  caller narrows on `err.body.error` to render the honest specific cause,
 *  never a single generic "that didn't work." This type documents the exact
 *  vocabulary so a caller doesn't have to hand-guess the server's error
 *  strings; it is not itself the resolved value of a call (redeemPromoCode's
 *  return type is only ever the success shape above — a failure throws). */
export type PromoRedeemErrorCode =
  | 'invalid_code'
  | 'expired_code'
  | 'exhausted_code'
  | 'already_redeemed'
  | 'invalid_input';

/** POST /api/promo/redeem {code} — promo.routes.ts. requireAuth-gated,
 *  rate-limited server-side (ai_expensive). The caller sends whatever the
 *  user typed verbatim — normalization (case/dash-insensitivity) happens
 *  entirely server-side (lib/promoCode.ts's normalizeCode), so this client
 *  never pre-processes the string itself. */
export async function redeemPromoCode(code: string): Promise<PromoRedeemResult> {
  return apiFetch<PromoRedeemResult>('/promo/redeem', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** POST /api/billing/checkout's response — billing.routes.ts:139-193. Three
 *  distinct shapes, matched exactly (never collapsed into one "did it work"
 *  boolean, since the FE must render each differently):
 *    - {configured:false} — no Stripe keys at all yet.
 *    - {configured:false, plan, reason:'price_not_configured'} — Stripe IS
 *      configured, but THIS plan's price id isn't set as a Workers var yet
 *      (resolvePriceId returned null) — a plan-specific gap, not a global one.
 *    - {soldOut:true, plan:'founder'} — the founder cap (isFounderCapReached)
 *      was already hit; ONLY possible for plan:'founder', never any other plan.
 *    - {configured:true, checkoutUrl, sessionId} — the real Stripe Checkout
 *      Session; the caller redirects the browser to checkoutUrl. */
export type CheckoutResult =
  | { configured: false }
  | { configured: false; plan: SubscriptionPlanName; reason: 'price_not_configured' }
  | { soldOut: true; plan: 'founder' }
  | { configured: true; checkoutUrl: string | null; sessionId: string };

/** POST /api/billing/checkout {plan} — billing.routes.ts:140. Body shape
 *  mirrors CheckoutInput's zod schema exactly (billing.routes.ts:135-137):
 *  `{plan}`, one of the 4 locked plan names. */
export async function createCheckoutSession(plan: SubscriptionPlanName): Promise<CheckoutResult> {
  return apiFetch<CheckoutResult>('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
}

/** POST /api/billing/portal's response — billing.routes.ts:203-224. A 404
 *  (`{error:'no_stripe_customer', hint:'subscribe first'}`) is a real,
 *  expected shape for a user who has never checked out — apiFetch throws an
 *  ApiError for it (non-2xx), which the caller renders as "subscribe first,"
 *  never a generic crash. */
export type PortalResult = { configured: false } | { configured: true; portalUrl: string };

/** POST /api/billing/portal — billing.routes.ts:199. No body. */
export async function createPortalSession(): Promise<PortalResult> {
  return apiFetch<PortalResult>('/billing/portal', { method: 'POST' });
}

/** Mirrors apps/api/lib/voiceTuning.ts's VoiceTuning dial shape exactly, as
 *  read by GET /api/voice/tuning (voice.routes.ts:54-58) — the settle-timing
 *  dials (cooldown/addition-window/carry — the ConversationSession debounce
 *  knobs), rendered READ-ONLY here (the brief: "render the dials read-only")
 *  — no PUT call from this screen, even though voice.routes.ts exposes one. */
export interface VoiceTuning {
  cooldownMs: number;
  additionWindowMs: number;
  carryMaxMs: number;
  carryAudibleMs: number;
}

/** GET /api/voice/tuning's response shape — voice.routes.ts:57
 *  (`{ok, tuning, defaults}`). `defaults` is served alongside the resolved
 *  tuning so a caller COULD show "this dial is at its default" — unused by
 *  this read-only render, kept in the type for shape fidelity. */
export interface VoiceTuningResponse {
  ok: boolean;
  tuning: VoiceTuning;
  defaults: VoiceTuning;
}

/** GET /api/voice/tuning — voice.routes.ts:54. requireAuth-gated. */
export async function getVoiceTuning(): Promise<VoiceTuningResponse> {
  return apiFetch<VoiceTuningResponse>('/voice/tuning');
}

/** Mirrors apps/api/lib/voiceTuning.ts's TTS_PIN shape exactly, as read by
 *  GET /api/voice/tts-pin (voice.routes.ts:83-90) — the W1-13 pin block. This
 *  screen renders only `voiceId` (the brief: "the pin's voiceId") — the other
 *  fields (modelId/stability/similarityBoost/style/useSpeakerBoost) are typed
 *  here for shape fidelity with the real response but not separately surfaced. */
export interface TtsPin {
  voiceId: string;
  modelId: string;
  stability: number;
  similarityBoost: number;
  style: number;
  useSpeakerBoost: boolean;
}

/** GET /api/voice/tts-pin's response shape — voice.routes.ts:89
 *  (`{ok, pin, defaults, epoch}`). `epoch` is the clip-cache regen counter
 *  (bumped on every pin PUT) — typed here, not rendered (this screen never
 *  writes a pin, so there is nothing that would need to react to its own bump). */
export interface TtsPinResponse {
  ok: boolean;
  pin: TtsPin;
  defaults: TtsPin;
  epoch: number;
}

/** GET /api/voice/tts-pin — voice.routes.ts:83. requireAuth-gated. */
export async function getTtsPin(): Promise<TtsPinResponse> {
  return apiFetch<TtsPinResponse>('/voice/tts-pin');
}

/** GET /api/account/export's full response shape — account.routes.ts:136-160
 *  (the exact object literal the route returns). This screen does not parse
 *  or render this body — the export BUTTON triggers a raw-fetch file download
 *  (see downloadAccountExport below) so the browser's native save-as flow
 *  handles a corpus of arbitrary size rather than this app holding the whole
 *  JSON blob in React state. The type is still declared here (rather than
 *  left as `unknown`) so a future screen that DOES want to render a preview
 *  has the real shape on hand, verified directly against the route. */
export interface AccountExportBundle {
  exportedAt: string;
  userId: string;
  corpus: Record<string, unknown[]>;
  dna: Record<string, unknown[]>;
  jobs: unknown[];
  applications: unknown[];
  letters: Record<string, unknown[]>;
  presenceProfiles: unknown[];
  subscriptionsStatus: unknown[];
}

/** GET /api/account/export — account.routes.ts:75. Triggers a real browser
 *  file download (Blob + a same-tick `<a download>` click, then revokes the
 *  object URL) rather than returning the parsed JSON, since the button's job
 *  is "give me a file," not "show me data." Deliberately bypasses apiFetch
 *  (which always calls response.json()) and does its own fetch + blob() so
 *  large export bodies never round-trip through JSON.parse/stringify twice.
 *  Re-throws a plain Error with the server's status on a non-ok response
 *  (mirrors ApiError's "never silently swallow" law) so the caller can show
 *  a real error instead of a silently-missing download. */
export async function downloadAccountExport(): Promise<void> {
  const response = await fetch(`${API_BASE}/account/export`, {
    credentials: 'include',
    headers: browserTimeZone() ? { 'X-Time-Zone': browserTimeZone() as string } : {},
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'unknown' }));
    const message =
      typeof body === 'object' && body && 'error' in body && typeof body.error === 'string'
        ? body.error
        : response.statusText;
    throw new ApiError(response.status, message, body);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `career-optimizer-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** POST /api/account/delete-request's response — account.routes.ts:195-216
 *  (the exact object literal). `willBeRemoved` and `note` are the SERVED
 *  truth this screen renders VERBATIM (the brief: "render the 30-day truth
 *  verbatim") — never a client-authored paraphrase of what deletion means. */
export interface DeleteRequestResult {
  status: 'deletion_requested';
  requestedAt: string;
  scheduledPurgeAt: string;
  windowDays: number;
  willBeRemoved: string[];
  note: string;
}

/** POST /api/account/delete-request — account.routes.ts:167. No body. */
export async function requestAccountDeletion(): Promise<DeleteRequestResult> {
  return apiFetch<DeleteRequestResult>('/account/delete-request', { method: 'POST' });
}

/** POST /api/account/delete-cancel's response — account.routes.ts:243
 *  (`{status:'deletion_canceled', wasPending}`). Always 200, even with
 *  nothing pending (account.routes.ts's own comment: "a client never has to
 *  check state first before offering 'cancel'"). */
export interface CancelDeletionResult {
  status: 'deletion_canceled';
  wasPending: boolean;
}

/** POST /api/account/delete-cancel — account.routes.ts:225. No body. */
export async function cancelAccountDeletion(): Promise<CancelDeletionResult> {
  return apiFetch<CancelDeletionResult>('/account/delete-cancel', { method: 'POST' });
}

// =============================================================================
// DATA REGION (GAP 1, data-residency Phase 0 — docs/design/
// data-residency-plan-2026-07-05.md "Detect → confirm"). The backend is
// LIVE (R227/R228, api 528ff8d8 — verified: account.routes.ts:271-347 +
// account.routes.dataRegion.test.ts) but had no client surface until this
// lane. Mirrors the unset/suggested/confirmed state tuple exactly — a
// suggestion is NEVER promoted to a decision by this client; only an
// explicit confirm call writes dataRegion.
// =============================================================================

/** The set of regions the server actually accepts (account.routes.ts:265,
 *  `VALID_REGIONS`) — kept here as a literal union rather than re-deriving
 *  it from a shared export (the route file doesn't export its allowlist),
 *  so if the server's set ever grows, a 400 here is the honest signal to
 *  update this literal to match, never a silently-stale guess rendered as
 *  if it were the true accepted set. */
export const DATA_REGIONS = ['US', 'AU', 'CA', 'EU', 'UK'] as const;
export type DataRegionValue = (typeof DATA_REGIONS)[number];

/** GET /api/account/data-region's response shape — account.routes.ts:274-304
 *  (the exact object literal). `state` is the server's own derivation
 *  (dataRegion set → 'confirmed'; else suggestedDataRegion set →
 *  'suggested'; else 'unset') — this client never re-derives it from the
 *  other two fields, it renders the served state verbatim. */
export interface DataRegionState {
  state: 'unset' | 'suggested' | 'confirmed';
  dataRegion: DataRegionValue | null;
  suggestedDataRegion: DataRegionValue | null;
  dataRegionConfirmedAt: string | null;
}

/** GET /api/account/data-region — account.routes.ts:274. No body. */
export async function getDataRegion(): Promise<DataRegionState> {
  return apiFetch<DataRegionState>('/account/data-region');
}

/** POST /api/account/data-region/confirm — account.routes.ts:311. THE ONLY
 *  write path to dataRegion (per the plan doc's "Detect → confirm" law): the
 *  caller must name the region explicitly, even if it matches a suggestion
 *  already on file — this function never auto-fills from
 *  suggestedDataRegion, the CALLER decides what to send. */
export async function confirmDataRegion(region: DataRegionValue): Promise<DataRegionState> {
  return apiFetch<DataRegionState>('/account/data-region/confirm', {
    method: 'POST',
    body: JSON.stringify({ region }),
  });
}

// =============================================================================
// TEMPLATE PREFERENCE (TEMPLATES-P2, Decision 9) — GET/PATCH
// /api/account/template-preference. Mirrors the data-region GET/confirm pair
// just above byte-for-byte in shape: `family`/`accent` are the server's OWN
// resolved values (the documented default already applied — no-frontend-
// bandaids: this client never re-derives the fallback), `storedFamily`/
// `storedAccent` are the honest raw column values (which may be null, or a
// real-but-not-yet-built family) for a caller that wants to tell "no
// preference set" apart from "set to something not active yet."
// ======================================================================


/** account.routes.ts's GET/PATCH /template-preference response shape
 *  (verified directly against that route's own `c.json({...})` literals). */
export interface TemplatePreferenceState {
  family: string;
  storedFamily: string | null;
  accent: string;
  storedAccent: string | null;
}

/** GET /api/account/template-preference — the current preference pair, with
 *  the server's own documented default already resolved. */
export async function getTemplatePreference(): Promise<TemplatePreferenceState> {
  return apiFetch<TemplatePreferenceState>('/account/template-preference');
}

/** PATCH /api/account/template-preference { family?, accentKey? } — THE ONLY
 *  write path to users.preferred_template_family / users.preferred_accent.
 *  Either or both may be supplied; each updates independently (a caller
 *  changing only the accent swatch doesn't need to re-send the family, and
 *  vice versa). Server-validated against the real family/accent vocabularies
 *  — an unknown value 400s rather than silently no-op'ing. */
export async function saveTemplatePreference(patch: {
  family?: string;
  accentKey?: string;
}): Promise<TemplatePreferenceState> {
  return apiFetch<TemplatePreferenceState>('/account/template-preference', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}
// EMAIL GATEWAY (GATEWAY-API, docs/design/email-door-2026-07-05.md) —
// "Forward jobs to Mira" (Settings.tsx). Mirrors account.routes.ts's own
// GET /email-gateway + POST /email-gateway/regenerate shapes exactly.
//
// SECRETS DISCIPLINE: `address` is the PLAINTEXT `mira+{token}@<domain>`
// address, present ONLY the moment it's first minted (either the FIRST GET
// after signup, structurally impossible since GET never mints — so in
// practice ONLY the regenerate response) — it is MASKED (null) on every
// subsequent GET, since the server stores only a one-way hash and cannot
// reconstruct it. The card renders this honestly: "generate a fresh one to
// see it again" once masked, never a placeholder pretending to be the real
// address.
// =============================================================================

export interface EmailGatewayState {
  hasToken: boolean;
  /** The plaintext address, or null when masked (see module header) or when
   *  no token has ever been minted (hasToken:false). */
  address: string | null;
  createdAt: string | null;
  /** KNOWN-SENDER: the bare agent address (mira@<domain>) that accepts mail
   *  from the user's own sign-in email with zero setup (DMARC + registered
   *  From). Served by the API from AGENT_EMAIL_DOMAIN — null when the
   *  gateway domain is unconfigured, and the card's copy honestly omits it. */
  bareAddress?: string | null;
}

/** GET /api/account/email-gateway — account.routes.ts. No body. */
export async function getEmailGateway(): Promise<EmailGatewayState> {
  return apiFetch<EmailGatewayState>('/account/email-gateway');
}

/** POST /api/account/email-gateway/regenerate — account.routes.ts. Revokes
 *  any currently-live token (kept forever, nothing-unrecoverable) and mints
 *  a fresh one — the OLD address stops working IMMEDIATELY. The caller
 *  (Settings.tsx) surfaces the warm warning BEFORE calling this; this
 *  function itself has no confirmation step of its own. */
export async function regenerateEmailGatewayToken(): Promise<EmailGatewayState> {
  return apiFetch<EmailGatewayState>('/account/email-gateway/regenerate', { method: 'POST' });
}

// =============================================================================
// APPLICATIONS — the Track tab's board (apps/api/routes/applications.routes.ts).
// NO backend changes (this brief's own law (d)) — every field below is EXACTLY
// what GET /applications / POST /applications / PATCH /applications/:id already
// serve. This is a FRESH type (not copied from apps/web/src/lib/api.ts's own
// Application, api.ts:1803-1817) because that sibling type is STALE: it predates
// W1-7 (2026-07-02) and is missing outcomeResult/outcomeFeedback plus the
// silenceDays/nudgeTier/closeOffered fields the route computes at read time
// (applications.routes.ts:119-137) — this type is verified directly against
// that route's CURRENT `c.json({ applications: withSilence })` shape, not
// against the older sibling FE.
// =============================================================================

/** Mirrors packages/schema/src/applications.ts APPLICATION_STATUSES exactly —
 *  the Tracker board's six columns, in board order. */
export const APPLICATION_STATUSES = ['wishlist', 'applied', 'screen', 'onsite', 'offer', 'closed'] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export const APPLICATION_STATUS_LABEL: Record<ApplicationStatus, string> = {
  wishlist: 'Wishlist',
  applied: 'Applied',
  screen: 'Screen',
  onsite: 'Onsite',
  offer: 'Offer',
  closed: 'Closed',
};

/** Mirrors packages/schema/src/applications.ts APPLICATION_OUTCOME_RESULTS —
 *  a TERMINAL result the user records themselves (never system-inferred). */
export const APPLICATION_OUTCOME_RESULTS = [
  'no_callback',
  'rejected',
  'withdrawn',
  'offer_accepted',
  'offer_declined',
] as const;
export type ApplicationOutcomeResult = (typeof APPLICATION_OUTCOME_RESULTS)[number];

/** W1-7 nudge tier — mirrors apps/api/lib/applicationSilence.ts's NudgeTier.
 *  'gentle' >= tier1Days (default 7), 'firm' >= tier2Days (14), 'stale' >=
 *  tier3Days (21) — policy-resolved, not hardcoded on the server; this client
 *  never re-derives a tier, it only renders the one the server already computed. */
export type NudgeTier = 'gentle' | 'firm' | 'stale';

export interface Application {
  id: string;
  jobId: string;
  resumeId: string | null;
  coverLetterId: string | null;
  status: ApplicationStatus;
  appliedAt: string | null;
  closedAt: string | null;
  notes: string | null;
  /** W1-7 additive — the user's OWN account of what happened; null = not yet
   *  told us, never system-guessed (applications.ts:55-61 doc comment). */
  outcomeResult: ApplicationOutcomeResult | null;
  outcomeFeedback: string | null;
  /** Joined from the job on GET /applications (leftJoin — null if the job was removed). */
  jobTitle?: string | null;
  jobCompany?: string | null;
  createdAt: string;
  updatedAt: string;
  /** W1-7 crickets lane (applications.routes.ts:119-137), computed FRESH at
   *  read time — null for every status except 'applied' (see
   *  isSilenceEligible), or when the application hasn't cleared even the
   *  gentlest tier yet. Never a fabricated day-count. */
  silenceDays: number | null;
  nudgeTier: NudgeTier | null;
  /** True ONLY at the 'stale' tier — an OFFER to mark the application closed,
   *  never an auto-move (hard rule #1: nothing here auto-applies a status
   *  change). The PATCH endpoint below is what a user's explicit "yes, close
   *  it" click actually calls. */
  closeOffered: boolean;
  /** TRACK-FIDELITY (2026-07-06) — whether `jobId` still resolves through
   *  the SAME shared loader (loadJobForUser, apps/api/lib/jobReads.ts) that
   *  GET /jobs/:id itself calls to render the rich detail page. true means
   *  `jobId` is a live, navigable `/jobs/:id` target — the fix for the
   *  owner's "loses all its fidelity once tracked" report, which traced to
   *  Tracker having no click-through at all plus this router never
   *  resolving through the canonical loader. false means the job is
   *  genuinely gone from both the legacy table and the lake for this user
   *  (an orphaned application) — render an honest "no longer available"
   *  state, never a dead click or an invented detail (NO INVENTION). */
  jobAvailable: boolean;
}

export interface ApplicationEvent {
  id: string;
  applicationId: string;
  eventType: 'created' | 'status_changed';
  occurredAt: string;
  summary: string | null;
  metadata: Record<string, unknown> | null;
}

/** GET /applications — applications.routes.ts:69. Most-recently-touched
 *  first (ORDER BY updatedAt DESC); this client never re-sorts. */
export async function listApplications(): Promise<Application[]> {
  const result = await apiFetch<{ applications: Application[] }>('/applications');
  return result.applications;
}

/** POST /applications — the explicit human-in-the-loop "Track" action
 *  (applications.routes.ts:185, TrackInput). Idempotent: one application per
 *  (user, job) — re-tracking an already-tracked job returns the EXISTING row
 *  with `created: false`, never a duplicate. */
export async function trackApplication(input: {
  jobId: string;
  resumeId?: string;
  coverLetterId?: string;
  status?: ApplicationStatus;
  notes?: string;
}): Promise<{ application: Application; created: boolean }> {
  return apiFetch('/applications', { method: 'POST', body: JSON.stringify(input) });
}

/** PATCH /applications/:id — move an application to a new stage
 *  (applications.routes.ts:245, TransitionInput). `status` is REQUIRED (a
 *  no-op PATCH to the same status is a valid, tested case — see
 *  applicationTransition.test.ts); notes/outcomeResult/outcomeFeedback are
 *  independently-settable additive fields, only sent when the caller actually
 *  supplies them (an omitted field leaves that column untouched server-side). */
export async function transitionApplication(
  id: string,
  status: ApplicationStatus,
  extra?: { notes?: string; outcomeResult?: ApplicationOutcomeResult; outcomeFeedback?: string },
): Promise<{ application: Application }> {
  return apiFetch(`/applications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, ...extra }),
  });
}

/** GET /applications/:id — one application + its full event history (the
 *  audit trail; applications.routes.ts:307). Not called by the board itself
 *  (the list already carries everything the board renders) — kept for a
 *  future per-application detail/history view. */
export async function getApplication(
  id: string,
): Promise<{ application: Application; events: ApplicationEvent[] }> {
  return apiFetch(`/applications/${id}`);
}

// =============================================================================
// OUTREACH — the Track tab's follow-up draft button
// (apps/api/routes/outreach.routes.ts POST /draft, kind:'follow_up', W1-7).
// NO backend changes. The follow-up path is GROUNDED ONLY in the tracked
// application's own real facts (the applied date, the SPECIFIC letter that
// application used) — an insufficiently-grounded request REFUSES with a
// served reason (422, FollowUpRefusalReason) rather than fabricating "just
// checking in" copy; this client renders that refusal verbatim, per the brief.
// =============================================================================

/** Mirrors apps/api/services/followUpComposer.ts's FollowUpRefusalReason
 *  exactly — the ONLY reasons a follow-up draft can be refused for. */
export type FollowUpRefusalReason =
  | 'not_tracked'
  | 'not_yet_applied'
  | 'no_letter_on_record'
  | 'letter_not_found'
  | 'empty_letter';

/** A successfully created draft (either kind). Mirrors outreach.routes.ts's
 *  deliverDraft return shape (the outreachDrafts row, `.returning()`). */
export interface OutreachDraft {
  id: string;
  userId: string;
  jobId: string;
  contactId: string | null;
  kind: 'application' | 'follow_up';
  subject: string;
  body: string;
  status: string;
  gmailDraftId: string | null;
  gmailWebUrl: string | null;
  threadId: string | null;
  createdAt: string;
  updatedAt: string;
  /** How it was delivered: 'gmail' | 'resend' (W1-15, served). */
  deliveredVia?: string | null;
}

/** The full discriminated outcome of POST /outreach/draft — success (201, the
 *  created draft) OR one of the route's own served failure shapes: a follow-up
 *  refusal (422, `reason` + `message` — render BOTH verbatim, never just one),
 *  Gmail not connected yet (409), or Gmail needing re-consent (403). Every
 *  branch is a REAL server response, never a client-invented default. */
export type DraftOutreachResult =
  | { ok: true; draft: OutreachDraft }
  | { ok: false; kind: 'follow_up_ungrounded'; reason: FollowUpRefusalReason; message: string }
  | { ok: false; kind: 'gmail_not_connected'; hint: string }
  | { ok: false; kind: 'gmail_reconsent_required'; hint: string };

/** POST /outreach/draft — outreach.routes.ts:271. `kind:'follow_up'` composes
 *  from the TRACKED application's own letter + real dates (composeFollowUpDraft,
 *  W1-7); `kind:'application'` composes from the job's latest tailored cover
 *  letter (the W1-1 path, unchanged). Either way: pushes to the user's OWN
 *  Gmail Drafts folder — nothing here ever sends. Every non-2xx response this
 *  route can return is parsed into DraftOutreachResult's discriminated failure
 *  branches rather than thrown as a generic ApiError, so the caller can render
 *  the EXACT served reason/message/hint (the brief's "render the refusal
 *  reasons — render them verbatim" law) instead of a flattened error string. */
export async function draftOutreach(input: {
  jobId: string;
  kind: 'application' | 'follow_up';
  contactId?: string;
}): Promise<DraftOutreachResult> {
  try {
    const result = await apiFetch<{ draft: OutreachDraft }>('/outreach/draft', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return { ok: true, draft: result.draft };
  } catch (err) {
    if (err instanceof ApiError && err.body && typeof err.body === 'object') {
      const body = err.body as Record<string, unknown>;
      if (body.error === 'follow_up_ungrounded') {
        return {
          ok: false,
          kind: 'follow_up_ungrounded',
          reason: body.reason as FollowUpRefusalReason,
          message: typeof body.message === 'string' ? body.message : 'Could not draft a follow-up.',
        };
      }
      if (body.error === 'gmail_not_connected') {
        return {
          ok: false,
          kind: 'gmail_not_connected',
          hint: typeof body.hint === 'string' ? body.hint : 'sign in to connect Gmail',
        };
      }
      if (body.error === 'gmail_reconsent_required') {
        return {
          ok: false,
          kind: 'gmail_reconsent_required',
          hint: typeof body.hint === 'string' ? body.hint : 'reconnect Gmail',
        };
      }
    }
    // Any other failure (network error, 400 invalid_input, 404 job_not_found,
    // 503 no_gmail_config, etc.) is a genuine unexpected error — propagate it
    // as before rather than inventing a DraftOutreachResult branch for it.
    throw err;
  }
}

// =============================================================================
// TAILOR — JobDetail's "Tailor my resume for this role" button
// (apps/api/routes/jobs.routes.ts POST /jobs/:id/tailor). NO backend changes.
// Typed to exactly what that route's final `c.json({...})` sends (jobs.routes
// .ts:659-674) — additive fields (lintGate/blocked/grounding) are OPTIONAL
// here too, present ONLY when the server actually included them (a passing,
// fully-grounded response omits them entirely, byte-identical to the base
// shape), so this client never assumes a gate ran when the server didn't say so.
// =============================================================================

export interface CvBriefGapTranslation {
  gap: string;
  translation: string;
}

/** Mirrors apps/api/services/TailoringService.ts's CvBrief exactly. */
export interface TailorCvBrief {
  summaryLine: string;
  leadAchievements: string[];
  gapTranslations: CvBriefGapTranslation[];
}

/** Mirrors apps/api/services/BrevityService.ts's BrevityResult — only the
 *  fields JobDetail actually renders are typed strictly; the rest of the
 *  service's richer internals (au/vernacular/sellShort/voiceCritique) are
 *  intentionally left untyped here (this client renders none of them), so a
 *  future internal TailoringService change can't force an unrelated edit here. */
export interface TailorBrevity {
  wordCount: number;
  fkGradeLevel: number;
  withinBudget: boolean;
  flags: string[];
}

/** AXIS-P3 — mirrors apps/api/services/TailoringService.ts's LensRankingComparison
 *  verbatim (field names not renamed). No existing cross-package import pattern
 *  from apps/api into this file exists today (this file only imports types from
 *  @careeropt/schema), so this is a local mirror rather than a new import path. */
export interface TailorLensRankingComparison {
  applied: boolean;
  modelOrder: string[];
  lensOrder: string[];
  matchedCount: number;
}

export interface TailorResult {
  coverLetter: string;
  cvBrief: TailorCvBrief;
  tonePreset: string;
  brevity: TailorBrevity;
  modelId: string;
  durationMs: number;
  /** AXIS-P3 — additive, null/undefined unless the API passed a lensRanking
   *  comparison (see TailorLensRankingComparison's own doc comment). */
  lensRanking?: TailorLensRankingComparison | null;
  /** Honesty-reviewer swap (R332): the PRIMARY reviewer's verdict on the
   *  cover letter — same advisory posture and identical shape as the essay's
   *  bullshitReview (BullshitReview below). Served inside `tailoring` by
   *  POST /jobs/:id/tailor and /refine; absent on older rows. The FE renders
   *  every field VERBATIM — no client-side re-derivation. */
  bullshitReview?: BullshitReview;
}

/** One row of apps/api/services/LintGateService.ts's LintGateBlock — the
 *  brief's "gates honestly (blocked/lint states)" needs the per-block diffs to
 *  show WHY a render is blocked, not just that it is. */
export interface TailorLintDiff {
  kind: 'spelling' | 'ai_tell' | 'brevity';
  span?: string;
  reason: string;
  suggestion: string;
}

export interface TailorLintBlock {
  location: string;
  passed: boolean;
  diffs: TailorLintDiff[];
}

/** Present ONLY when the render-BLOCKING gate failed (jobs.routes.ts:667's
 *  `...(lintGate.passed ? {} : { lintGate, blocked: true })`) — a passing
 *  response omits this entirely. */
export interface TailorLintGate {
  passed: boolean;
  blockingCount: number;
  blocks: TailorLintBlock[];
}

/** Mirrors apps/api/services/grounding/TailoredProseGate.ts's ProseFlag —
 *  present only when a hard claim failed or an advisory flag fired
 *  (jobs.routes.ts:671-673's additive `grounding` field). 'entity_mismatch' +
 *  `entity` are additive (Phase B, GATE-1/GenerationGate.ts): a figure that IS
 *  real in the corpus but attributed to the WRONG company in the sentence
 *  (the FIFA-at-commercetools class). */
export interface TailorGroundingFlag {
  slot: 'coverLetterFigure' | 'gapTranslation';
  value: string;
  reason: 'ungrounded_figure' | 'ungrounded_narrative' | 'entity_mismatch';
  entity?: string | null;
}

export interface TailorResponse {
  ok: true;
  coverLetterId: string;
  scenarioId: string;
  scenarioName: string;
  tailoring: TailorResult;
  /** Present only on a FAILED lint gate — see TailorLintGate's doc comment. */
  lintGate?: TailorLintGate;
  blocked?: boolean;
  /** Present only when the grounding verdict is notable (grounded:false or a
   *  non-empty flags list) — the signal this UI uses to hold Preview/Download
   *  back and show the flags instead of silently trusting a clean render. */
  grounding?: { grounded: boolean; flags: TailorGroundingFlag[] };
}

/** POST /jobs/:id/tailor — jobs.routes.ts:419. Composes a cover letter +
 *  CV-framing brief under the resolved scenario lens, runs it through the
 *  grounding gate (P3, drops/nulls anything ungrounded) and the three linters
 *  (brevity/AU-spelling/anti-AI-tells) BEFORE returning it — this call is the
 *  entire round trip; there is no polling, no separate "check status" request. */
export async function tailorJob(jobId: string, scenarioId?: string): Promise<TailorResponse> {
  return apiFetch<TailorResponse>(`/jobs/${jobId}/tailor`, {
    method: 'POST',
    body: JSON.stringify(scenarioId ? { scenarioId } : {}),
  });
}

// =============================================================================
// THE ALIGNMENT ESSAY — "Why {company}?" (docs/design/alignment-essay-
// module-2026-07-05.md). Mirrors TailorResponse's own additive-field
// conventions (lintGate/blocked/grounding present ONLY on a notable verdict)
// field-for-field against jobs.routes.ts's POST /jobs/:id/alignment-essay.
// =============================================================================

/** Mirrors TailorGroundingFlag's shape for this artifact's own grounding
 *  gate (gateEssayProse — now GATE-1/GenerationGate.ts underneath: hard
 *  entity/figure binding PLUS the pre-existing soft narrative check, since
 *  the essay has no CV-brief hard-field sibling). 'ungrounded_figure' +
 *  'entity_mismatch' are additive (Phase B) — see TailorGroundingFlag's own
 *  doc comment for the entity_mismatch case (the FIFA-at-commercetools class). */
export interface EssayGroundingFlag {
  reason: 'ungrounded_narrative' | 'ungrounded_figure' | 'entity_mismatch' | 'user_corrected';
  value: string;
  entity?: string | null;
  /** Set only when reason === 'user_corrected' (Phase C/C-P2) — the prior
   *  disputed correction's own flaggedText, so the panel can say WHY: "you
   *  already told us this wasn't right." */
  correctedClaim?: string | null;
}

export interface AlignmentEssayResponse {
  ok: true;
  essayId: string;
  body: string;
  wordCount: number;
  targetWords: number;
  maxWords: number;
  cultureSourceId: string | null;
  cultureSourceUrl: string | null;
  /** True when no verified company-values source existed at generation time
   *  — the honest drivers-only state (the brief's own §"nothing verifiable"
   *  branch). The FE offers a paste box when this is true. */
  cultureSourceMissing: boolean;
  /** The server's own ready-made provenance sentence (AlignmentEssayService.
   *  formatCultureProvenanceLine), worded per sourceKind ONE place, backend-
   *  side — e.g. "Grounded in what Anthropic says about itself in this
   *  posting." Absent when cultureSourceMissing (nothing to attribute). The
   *  FE renders this VERBATIM rather than re-deriving its own wording from
   *  cultureSourceUrl, so a future new sourceKind's phrasing never drifts
   *  out of sync between server and client. */
  cultureSourceProvenance?: string;
  /** True when the one allowed compress-to-max revision pass ran (the essay
   *  came back over maxWords on the first draft). */
  revised: boolean;
  /** Present only when the render-BLOCKING gate failed — same shape/
   *  convention as TailorResponse.lintGate. */
  lintGate?: TailorLintGate;
  blocked?: boolean;
  /** Present only when STILL over maxWords after the one allowed revision
   *  pass — the honest over-budget state, never silently rendered long. */
  overBudget?: boolean;
  /** Present only when the grounding verdict is notable (grounded:false or
   *  a non-empty flags list). */
  grounding?: { grounded: boolean; flags: EssayGroundingFlag[] };
  /** THE FORWARD-LOOKING LAW's own guard verdict (docs/design/alignment-
   *  essay-module-2026-07-05.md's tail section; AlignmentEssayService.
   *  enforceForwardLookingOpening). 'forward' = the opening never read as an
   *  origin-story/autobiography-of-the-journey framing (or the one allowed
   *  regeneration pass fixed it). 'backward' = it STILL read that way after
   *  the one regeneration pass — the essay still renders (this never blocks,
   *  unlike overBudget), it is an honest quality flag only. A PER-
   *  GENERATION-RUN verdict — same posture as lintGate/overBudget/grounding
   *  above, so it is likewise absent from the hydration read
   *  (LatestAlignmentEssay below) rather than replayed/guessed for an old
   *  row that never ran this guard. Always present on a fresh POST (unlike
   *  the conditionally-additive fields above) since 'forward' is itself
   *  meaningful — its absence would be ambiguous between "checked, passed"
   *  and "never checked"; marked optional here only because a hydrated
   *  result never populates it. */
  openingStyle?: 'forward' | 'backward';
  /** THE READER'S CHAIR — docs/design/bullshit-detector-2026-07-05.md;
   *  BullshitDetectorService.runBullshitDetectorLoop's own final-pass result.
   *  Present only when the FINAL detect pass (post-regen, if the one allowed
   *  regeneration ran) still has findings — never blocks the render (the
   *  essay body above is still the user's to use), same advisory posture as
   *  overBudget/grounding/openingStyle. The FE renders every field here
   *  VERBATIM (quote/category/severity/reaction are all server-computed) —
   *  NO client-side re-derivation. Absent from the hydration read
   *  (LatestAlignmentEssay below), same reasoning as openingStyle's own
   *  doc comment: a per-generation-run verdict, never replayed for an old row. */
  bullshitReview?: BullshitReview;
  /** Phase C/C-P3 (staleness fan-out) — mirrors LatestAlignmentEssay's own
   *  field. Absent on a fresh POST response (the server never computes it
   *  there — a just-generated essay cannot yet be stale from a correction,
   *  so the field is naturally missing rather than a wasted always-false
   *  query); present (and meaningful) only on a hydrated read, where the
   *  panel copies it in from LatestAlignmentEssay. */
  staleFromCorrection?: boolean;
}

/** Mirrors BullshitDetectorService.BullshitFinding's own shape field-for-
 *  field. `source` distinguishes a free deterministic lexical hit from a
 *  paid model-pass finding — useful for the FE to label its chip (and for
 *  telemetry), never used to alter what's rendered. */
export interface BullshitFinding {
  quote: string;
  category: 'slop' | 'smoke' | 'buried_lede' | 'generic' | 'unsupported_claim';
  severity: 'low' | 'medium' | 'high';
  reaction: string;
  source: 'lexical' | 'model';
}

export interface BullshitReview {
  overallVerdict: string;
  wouldKeepReading: boolean;
  findings: BullshitFinding[];
  /** True when the one allowed bullshit-detector regeneration pass ran. */
  revised: boolean;
}

export interface AlignmentEssayRequest {
  targetWords?: number;
  maxWords?: number;
  cultureSourceId?: string;
  pastedCulture?: string;
  toneNotes?: string;
  /** Present on a react-to-refine regen — the SAME route re-enters (the
   *  loop law) rather than a separate refine endpoint. */
  refineInstructions?: string;
}

/** POST /jobs/:id/alignment-essay — jobs.routes.ts. Composes the essay under
 *  the resolved DNA overlay + declared direction (person side) and the
 *  resolved culture source (company side, or honestly absent), runs it
 *  through the SAME brevity/AI-tell linter gate + a soft grounding gate
 *  tailor/refine already run, and persists an append-only regen-chain row.
 *  This call is the entire round trip — no polling. */
export async function generateAlignmentEssay(
  jobId: string,
  req?: AlignmentEssayRequest,
): Promise<AlignmentEssayResponse> {
  return apiFetch<AlignmentEssayResponse>(`/jobs/${jobId}/alignment-essay`, {
    method: 'POST',
    body: JSON.stringify(req ?? {}),
  });
}

/** The hydration read's own narrower shape — deliberately NOT
 *  AlignmentEssayResponse (no lintGate/blocked/overBudget/grounding — those
 *  are per-generation-run gate verdicts; a hydrated read never re-derives or
 *  replays a verdict it did not just compute). See GET /jobs/:id/
 *  alignment-essay/latest's own doc comment for the full reasoning. */
export interface LatestAlignmentEssay {
  essayId: string;
  body: string;
  wordCount: number;
  targetWords: number;
  maxWords: number;
  cultureSourceId: string | null;
  cultureSourceUrl: string | null;
  cultureSourceMissing: boolean;
  /** Same ready-made provenance sentence as AlignmentEssayResponse's own
   *  field — hydrated from the persisted cultureSourceId, worded server-
   *  side. Absent when cultureSourceMissing. */
  cultureSourceProvenance?: string;
  createdAt: string;
  /** Phase C/C-P3 (staleness fan-out) — true when a claim_correction for this
   *  job postdates this essay (a simple server-side timestamp comparison,
   *  never a stored flag). ALWAYS present (unlike the POST response's
   *  conditionally-additive fields) — see the route's own doc comment for
   *  why true/false is itself meaningful here. The panel offers, never
   *  auto-regenerates: a banner + the SAME "Regenerate" action the user
   *  already has, never fired automatically. */
  staleFromCorrection: boolean;
}

/** GET /jobs/:id/alignment-essay/latest — jobs.routes.ts. The LATEST
 *  non-superseded essay row for this user + job, or `{ essay: null }` when
 *  none has ever been generated — the panel's hydration call, so a page
 *  reload (e.g. the Drive-connect OAuth round-trip) never silently loses a
 *  previously generated essay that was safely persisted all along. */
export async function getLatestAlignmentEssay(jobId: string): Promise<{ essay: LatestAlignmentEssay | null }> {
  return apiFetch<{ essay: LatestAlignmentEssay | null }>(`/jobs/${jobId}/alignment-essay/latest`);
}

// =============================================================================
// SAVE TO GOOGLE DRIVE — docs/design/drive-export-2026-07-05.md. Three thin
// calls to exports.routes.ts, mirroring this file's own house shape (a plain
// apiFetch wrapper per route, types matching the server's wire contract
// field-for-field — same pattern as getBillingEntitlement/createPortalSession
// just above). DriveSaveButton.tsx is the one caller.
//
// GAP-CLOSE (2026-07-05) widened this to add 'alignment_essay' — mirrors
// ExportArtifactType in DriveExportService.ts (the server's own union) so a
// mismatch between this file and the server can never silently pass an
// unsupported type through; the server's own allow-list (exports.routes.ts
// POST /drive) is still the actual enforcement, this is just the matching
// client-side type.
// =============================================================================

export type ExportArtifactType = 'resume' | 'cover_letter' | 'corpus_resume' | 'alignment_essay';

export interface DriveStatusResult {
  connected: boolean;
  folderId?: string;
  /** SEC-3 (Phase A) — present only when connected:false; an honest reason
   *  the receipt failed, never inferred client-side. 'not_connected' = no
   *  live grant row; 'token_unreadable' = a grant exists but the underlying
   *  secret didn't resolve (the exact "connected:true off the row alone"
   *  lie this route used to tell — see exports.routes.ts's own doc comment
   *  on GET /drive/status). */
  reason?: 'not_connected' | 'token_unreadable';
}

/** GET /api/exports/drive/status — whether this user has a live (non-revoked)
 *  Drive grant. Called on mount so the button never guesses its own initial
 *  phase. */
export async function driveStatus(): Promise<DriveStatusResult> {
  return apiFetch<DriveStatusResult>('/exports/drive/status');
}

export interface DriveAuthorizeUrlResult {
  url: string;
}

/** GET /api/exports/drive/authorize?return_to=… — mints the signed,
 *  drive.file consent URL for THIS authed user. `returnTo` should be the
 *  current screen's own path (e.g. `/jobs/${jobId}`) so the OAuth round trip
 *  lands the user back where they started saving, not a hardcoded page. */
export async function driveAuthorizeUrl(returnTo: string): Promise<DriveAuthorizeUrlResult> {
  // Carry THIS app's origin so the post-consent redirect lands back here — a
  // bare relative return_to resolves against the API's own origin, which
  // serves the old web SPA (the 2026-07-05 wrong-turn). Server-validated
  // against the sign-in flow's same origin allowlist.
  return apiFetch<DriveAuthorizeUrlResult>(
    `/exports/drive/authorize?return_to=${encodeURIComponent(returnTo)}&return_origin=${encodeURIComponent(window.location.origin)}`,
  );
}

export interface DriveExportResult {
  fileId: string;
  webViewLink: string;
}

/** POST /api/exports/drive { artifactType, artifactId, templateFamily?,
 *  accentKey? } — renders the artifact server-side, uploads it to the
 *  user's own Drive folder as a native Google Doc, and returns the real,
 *  served webViewLink (never a constructed/guessed URL — the button opens
 *  exactly what the server reports). A 409 { error: 'reconnect' |
 *  'not_connected' } is a normal, expected shape here — classifyDriveError
 *  (driveExportState.ts) turns it into an honest UI state, not a
 *  caught-and-swallowed exception.
 *
 *  TEMPLATES-P2: `templateFamily`/`accentKey` are OPTIONAL — every existing
 *  caller (omitting both) is byte-identical to before. A PREMIUM family
 *  without the template_library capability returns a 402 whose body matches
 *  requireEntitlement's own shape ({error:'entitlement_required',
 *  capability, tier, reason, hint}) — this is the SAME server-side gate
 *  every export path enforces (§6), never a client-only check. */
export async function driveExport(
  artifactType: ExportArtifactType,
  artifactId: string,
  templateFamily?: string,
  accentKey?: string | null,
): Promise<DriveExportResult> {
  return apiFetch<DriveExportResult>('/exports/drive', {
    method: 'POST',
    body: JSON.stringify({ artifactType, artifactId, templateFamily, accentKey }),
  });
}

// =============================================================================
// JOB-WALK-2 fix #3 — the relocation toggle's read/write pair. Thin, additive
// wrappers around GET/PUT /api/profile (apps/api/routes/profile.routes.ts) —
// the FIRST client-side callers of this endpoint from this app. Both routes
// return `{ profile: ResolvedUserProfile }` (profile.routes.ts's own
// `c.json({ profile })` on both handlers) — attributes is a FIELD on that
// nested profile, never the top-level response shape itself. Only the one
// field JobDetail's toggle actually needs is typed here (attributes, where
// declaredRelocation lives — ProtectedClaimsGate.ts's readDeclaredFacts),
// never the full resolved-profile shape (userProfile.ts's
// ResolvedUserProfile), so this stays a narrow, honest projection rather than
// a second competing profile type for this app.
// =============================================================================

export interface ProfileAttributesResult {
  profile: { attributes: Record<string, unknown> };
}

/** GET /api/profile — read just the attributes bag (declaredRelocation lives
 *  there). Used to resolve the toggle's initial on/off state on mount. */
export async function getProfileAttributes(): Promise<ProfileAttributesResult> {
  return apiFetch<ProfileAttributesResult>('/profile');
}

/** PUT /api/profile { declaredRelocation } — set or clear the user's declared
 *  relocation plan in their own words. `null` explicitly clears a prior
 *  declaration (the toggle's "off" state); the server normalizes a blank
 *  string to null too (normalizeDeclaredRelocation, profile.routes.ts).
 *  SUPERSEDED as JobDetail's own toggle write path by toggleJobRelocation
 *  below (RELOCATION-SCORE lane) — kept, unmodified, for any other caller
 *  that only needs the writing-flag behaviour without the score update. */
export async function setDeclaredRelocation(
  declaredRelocation: string | null,
): Promise<ProfileAttributesResult> {
  return apiFetch<ProfileAttributesResult>('/profile', {
    method: 'PUT',
    body: JSON.stringify({ declaredRelocation }),
  });
}

// =============================================================================
// HYPER-LOCAL (2026-07-06) — the declared home metro/city read+write pair.
// Same thin-wrapper shape as getProfileAttributes/setDeclaredRelocation
// immediately above (GET/PUT /api/profile, apps/api/routes/profile.routes.ts)
// — a narrow projection of ResolvedUserProfile, never a second competing
// profile type for this app. NEVER a street address; metro/city grain only
// (docs/design/hyper-local-home-2026-07-06.md's privacy stance).
// =============================================================================

export interface HomeMetroResult {
  profile: { homeMetro: string | null; homeAdminArea: string | null; homeCountry: string | null };
}

/** GET /api/profile — read just the home-metro fields (+ homeCountry, so the
 *  card can show "in <country>" context without a second request). Used to
 *  seed the declare card's initial state on mount. */
export async function getHomeMetro(): Promise<HomeMetroResult> {
  return apiFetch<HomeMetroResult>('/profile');
}

/** PUT /api/profile { homeMetro, homeAdminArea } — declare (or clear, via
 *  null) the user's home metro/city + qualifying region. Mirrors
 *  setDeclaredRelocation's null-clears convention exactly; the server
 *  additionally normalizes a blank string to null
 *  (normalizeDeclaredRelocation, profile.routes.ts) so a cleared field reads
 *  identically whichever side did the trimming. */
export async function setHomeMetro(args: {
  homeMetro: string | null;
  homeAdminArea: string | null;
}): Promise<HomeMetroResult> {
  return apiFetch<HomeMetroResult>('/profile', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

// =============================================================================
// RELOCATION-SCORE — the toggle becomes a real scoring input. POST /jobs/:id/
// relocation (jobs.routes.ts) does BOTH the existing writing-flag write
// (user_profiles.attributes.declaredRelocation, same shape setDeclaredRelocation
// above already produced) AND a deterministic, zero-Claude score recompute in
// ONE round trip — the dial updates instantly off this single response, no
// second fetch. baseScore/modifier/effectiveScore are served as SEPARATE named
// numbers (never a single mystery-moved score) so the FE can show the honest
// decomposition; `copy` is the served, verbatim one-line reason — this app
// never composes its own explanation from the raw numbers.
// =============================================================================

/** Why the modifier landed where it did — mirrors RelocationModifierReason
 *  (apps/api/lib/relocationModifier.ts) exactly; kept as a plain string union
 *  here (not re-exported from a shared package) the same way this file
 *  already keeps its own narrow response types alongside the API layer. */
export type RelocationModifierReason =
  | 'relocation_on_country_differs'
  | 'relocation_off_country_differs'
  | 'same_country'
  | 'job_country_unknown'
  | 'home_country_unknown';

/** W19 (guided-walk 2026-07-21/22) — the SAME decomposition shape, now served
 *  TWICE: once read-only on GET /jobs/:id (RelocationScoreSnapshot, below —
 *  no `ok`, nothing was written), and once as the write-confirming response
 *  of POST /jobs/:id/relocation (RelocationToggleResult, which is exactly
 *  this shape plus `ok: true`). Split out so the two response shapes can
 *  never drift apart field-by-field — see getJob's own doc comment for why
 *  the GET-time copy exists at all (closing "undefined copy-wise" for a
 *  user who never touches the toggle). */
export interface RelocationScoreSnapshot {
  relocationDeclared: boolean;
  jobId: string;
  scenarioId: string | null;
  /** The base story-fit score exactly as JobScoringService computed it —
   *  never edited by this route. Null when the job has never been scored. */
  baseScore: number | null;
  /** Signed points applied on top of baseScore — 0 when location isn't a
   *  live factor for this job (same country / unknown), negative when it is
   *  and relocation is off, 0 again the instant relocation is declared. */
  modifier: number;
  /** baseScore + modifier, clamped to [0, 100]. Null exactly when baseScore
   *  is null (never a fabricated placeholder score). This is the number the
   *  score dial should render. */
  effectiveScore: number | null;
  reason: RelocationModifierReason;
  /** True when the job's country and the user's home country both resolved
   *  AND differ — i.e. relocation is actually live for this job, regardless
   *  of which way the toggle sits. */
  locationIsFactor: boolean;
  /** The served, honest one-line reason — render verbatim, never re-composed
   *  client-side (no-frontend-bandaids). Always present — including the
   *  'job_country_unknown'/'home_country_unknown' honest strings — so the FE
   *  never has to fall back to a vaguer client-composed line. */
  copy: string;
}

export interface RelocationToggleResult extends RelocationScoreSnapshot {
  ok: true;
}

/** POST /jobs/:id/relocation { relocationDeclared } — flips the relocation
 *  toggle for ONE job and gets the recomputed effective score back in the
 *  SAME response (RELOCATION-SCORE lane, the owner's ruling: "an instant
 *  score update based off of the toggle switch"). Deterministic server-side
 *  — zero Claude calls, the base fit score is never re-run. */
export async function toggleJobRelocation(
  jobId: string,
  relocationDeclared: boolean,
): Promise<RelocationToggleResult> {
  return apiFetch<RelocationToggleResult>(`/jobs/${encodeURIComponent(jobId)}/relocation`, {
    method: 'POST',
    body: JSON.stringify({ relocationDeclared }),
  });
}

// =============================================================================
// CONSENTS — ENROLL lane (custom login + first-run enrollment consent). GET/
// POST /auth/consents — NOT under /api (mirrors the OAuth routes' own
// mount point, auth.routes.ts, app.route('/auth', authRouter) in server.ts).
// apiFetch already special-cases any path starting with '/auth/' to resolve
// through API_ORIGIN rather than API_BASE (see this file's own apiFetch
// comment) — the same routing every existing /auth/me / /auth/logout call
// already relies on, so these two functions need no new plumbing.
// =============================================================================

export type ConsentDocState = 'missing' | 'stale' | 'current';
export type ConsentDoc = 'eula' | 'privacy';

/** GET /auth/consents' per-doc status — mirrors the server's own
 *  resolveConsentDocStatus() shape (apps/api/lib/consentState.ts) verbatim;
 *  this client never re-derives `state`, it renders the served truth. */
export interface ConsentDocStatus {
  doc: ConsentDoc;
  state: ConsentDocState;
  currentVersion: string;
  accepted: { docVersion: string; acceptedAt: string } | null;
}

export interface ConsentStatusResult {
  statuses: ConsentDocStatus[];
  needsAction: boolean;
}

/** GET /auth/consents — auth.routes.ts. No body. */
export async function getConsentStatus(): Promise<ConsentStatusResult> {
  return apiFetch<ConsentStatusResult>('/auth/consents');
}

export interface ConsentAcceptResult {
  doc: ConsentDoc;
  docVersion: string;
  acceptedAt: string;
  via: 'enrollment' | 'grandfather_prompt';
}

/** POST /auth/consents { doc, docVersion } — records an explicit acceptance.
 *  `docVersion` must be exactly the CURRENT version GET /auth/consents just
 *  served for that doc (currentVersion) — the caller never invents or
 *  hand-types a version string; ConsentGate.tsx always passes through the
 *  value it just read. */
export async function postConsentAccept(
  doc: ConsentDoc,
  docVersion: string,
): Promise<ConsentAcceptResult> {
  return apiFetch<ConsentAcceptResult>('/auth/consents', {
    method: 'POST',
    body: JSON.stringify({ doc, docVersion }),
  });
}

// =============================================================================
// THE GAP-ADDRESS LOOP (docs/design/gap-address-loop-2026-07-05.md) —
// apps/api/routes/jobs.routes.ts's GET /jobs/:id/gaps + POST /jobs/:id/
// gap-address/{start,turn,commit}. "Click a gap, have a quick scoped
// conversation with Mira, the answer lands in the permanent corpus with
// provenance, then an automatic rescore." THIS FILE IS PURE PRESENTATION —
// every field below is exactly what the server served; nothing here is
// derived, classified, or invented client-side (the gap CLASS in particular
// is a server-computed field, never re-derived here).
// =============================================================================

export type GapClass = 'evidence' | 'situation';

export interface GapResolution {
  status: 'evidence_committed' | 'situation_declared' | 'dismissed';
  achievementId: string | null;
  declarationId: string | null;
  sessionId: string;
}

export interface ServedGap {
  id: string;
  text: string;
  gapClass: GapClass;
  addressed: boolean;
  resolution: GapResolution | null;
}

export interface GapsListResult {
  ok: true;
  gaps: ServedGap[];
  nextUnaddressed: ServedGap | null;
  allAddressed: boolean;
}

/** GET /jobs/:id/gaps — jobs.routes.ts. Read-only, no Claude call: classified
 *  gaps for the job's latest score + whether each has already been
 *  addressed (and by what — an achievement or a dated declaration), so a
 *  closed gap never resurfaces blind. */
export async function getJobGaps(jobId: string): Promise<GapsListResult> {
  return apiFetch<GapsListResult>(`/jobs/${jobId}/gaps`);
}

export interface GapAddressStartResult {
  ok: true;
  sessionId: string;
  gapClass: GapClass;
  question: string;
  readyToClose: boolean;
  /** W22 — the user's OWN vocabulary for this gap, mined verbatim server-side
   *  from the evidence the session froze (apps/api/lib/corpusTerms.ts). The
   *  dictation / spoken-answer repair pass corrects toward these terms and the
   *  curated technical lexicon, and toward nothing else. Optional: absent on an
   *  older server, and honestly EMPTY when the evidence carries no distinctive
   *  terms — the repair pass then runs on the curated lexicon alone. */
  knownTerms?: string[];
}

/** POST /jobs/:id/gap-address/start { gapId } — opens a scoped session for
 *  ONE gap and returns Mira's opening question (its wording differs by
 *  class server-side: evidence asks for the closest thing you did do;
 *  situation asks where things stand today). */
export async function startGapAddress(jobId: string, gapId: string): Promise<GapAddressStartResult> {
  return apiFetch<GapAddressStartResult>(`/jobs/${jobId}/gap-address/start`, {
    method: 'POST',
    body: JSON.stringify({ gapId }),
  });
}

export interface GapAddressTurnResult {
  ok: true;
  question: string;
  readyToClose: boolean;
}

/** POST /jobs/:id/gap-address/turn { sessionId, capture } — posts the user's
 *  answer (mic or typed, via the shared InputCapture shape) and returns
 *  Mira's next line. `readyToClose` true means enough has been said — the
 *  panel should offer to commit rather than keep asking. */
export async function gapAddressTurn(
  jobId: string,
  sessionId: string,
  capture: InputCapture,
): Promise<GapAddressTurnResult> {
  return apiFetch<GapAddressTurnResult>(`/jobs/${jobId}/gap-address/turn`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, capture }),
  });
}

export interface GapAddressCommitResult {
  ok: boolean;
  committed: boolean;
  achievementId?: string | null;
  declarationId?: string | null;
  rescored: boolean;
  rescoreError?: string | null;
  gaps: ServedGap[];
  nextUnaddressed: ServedGap | null;
  /** Present only on a 422 (the write-gate rejected an ungrounded answer, or
   *  nothing usable was said) — the honest reason, never a silent failure. */
  reason?: string;
  message?: string;
}

/** POST /jobs/:id/gap-address/commit { sessionId, dismiss? } — closes the
 *  conversation. Evidence closes into a corpus achievement (through the
 *  SAME no-invention write-gate the standalone respond-to-gap path uses);
 *  situation closes into a dated declaration of the user's own stated
 *  words. Either way triggers ONE automatic rescore
 *  (scoreJobUnderScenarios) before returning — the caller should render a
 *  "rescoring against this role…" wait state for the duration of this
 *  call, not a separate poll. Pass `dismiss: true` to close without
 *  persisting anything (an honest non-outcome, no rescore). */
export async function commitGapAddress(
  jobId: string,
  args: { sessionId: string; dismiss?: boolean },
): Promise<GapAddressCommitResult> {
  return apiFetch<GapAddressCommitResult>(`/jobs/${jobId}/gap-address/commit`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

// =============================================================================
// CLAIM CORRECTIONS — the "that's not right" affordance (Phase 2 Lane B, D12
// + the job-side half of D11: docs/design/fix-architecture-2026-07-08/
// {FIX-ARCHITECTURE.md,BUILD-PLAN.md} Class 4). USER-DRIVEN, NEVER AUTO-FIX:
// this never rewrites the claim's rendered text — the surface marks it
// corrected (a strikethrough/chip), the write is a wake event the interview
// voice picks up later, never a same-request rewrite.
// =============================================================================

export type ClaimCorrectionSourceType =
  | 'gap'
  | 'radar_axis'
  | 'through_line'
  | 'essay_line'
  | 'reasoning_bullet';

export type ClaimCorrectionRuling = 'disputed' | 'confirmed' | 'withdrawn';

export interface ClaimCorrectionRecord {
  id: string;
  sourceType: ClaimCorrectionSourceType;
  sourceId: string;
  flaggedText: string;
  userNote: string | null;
  ruling: ClaimCorrectionRuling;
  createdAt: string;
}

/** Builds the SAME "{sourceId} {flaggedText}" key the server folds
 *  corrections by (apps/api/services/corpus/claimCorrections.ts's
 *  claimFoldKey) — most of these surfaces have no per-bullet id, only the
 *  owning job does, so the exact claim text disambiguates. */
export function claimCorrectionKey(sourceId: string, flaggedText: string): string {
  return `${sourceId} ${flaggedText}`;
}

/** POST /corpus/claim-corrections — record one "that's not right" dispute (or
 *  a confirm/withdraw). ONE explicit human decision per call, never batched. */
export async function recordClaimCorrection(body: {
  sourceType: ClaimCorrectionSourceType;
  sourceId: string;
  flaggedText: string;
  userNote?: string;
  ruling: ClaimCorrectionRuling;
}): Promise<{ ok: true; correction: ClaimCorrectionRecord }> {
  return apiFetch(`/corpus/claim-corrections`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** GET /corpus/claim-corrections/:sourceType — every corrected claim of one
 *  family for this user, keyed by claimCorrectionKey so a surface can look
 *  up "is THIS exact bullet already corrected?" in one round trip. */
export async function getClaimCorrections(
  sourceType: ClaimCorrectionSourceType,
): Promise<{ corrections: Record<string, ClaimCorrectionRecord> }> {
  return apiFetch(`/corpus/claim-corrections/${encodeURIComponent(sourceType)}`);
}

// =============================================================================
// ADMIN-SWEEP (2026-07-06) — POST /api/admin/axis-affinities/backfill, the
// AXIS-P2 (R301) resumable-batch corpus-tagging sweep (apps/api/routes/
// admin-ingest.routes.ts). requireAuth+requireAdmin-gated server-side
// already — this client makes no admin decision of its own, it just calls
// the route; a non-admin caller gets the SAME flat 403 ApiError every other
// admin route already throws. ONE batch per call; the caller (useAxisSweep,
// lib/useAxisSweep.ts) loops it until isLastBatch is true.
// =============================================================================

export type AxisSweepSubjectType = 'achievement' | 'facet';

/** Mirrors AxisAffinityTaggerService.ts's AxisBackfillBatchResult verbatim
 *  (apps/api/services/AxisAffinityTaggerService.ts:691-700) plus the route's
 *  own `ok` wrapper (admin-ingest.routes.ts's `c.json({ ok: true, ...result })`).
 *  Every count here is the batch's OWN — never accumulated server-side across
 *  calls — so a caller that wants a running total (this app's sweep card)
 *  accumulates it itself from these per-batch numbers, never invents one. */
export interface AxisSweepBatchResult {
  ok: true;
  subjectType: AxisSweepSubjectType;
  processed: number;
  tagged: number;
  skippedTerminalOnly: number;
  totalDropped: number;
  isLastBatch: boolean;
}

/** POST /api/admin/axis-affinities/backfill { subjectType, batchSize? }.
 *  Runs exactly ONE resumable batch — see admin-ingest.routes.ts's own doc
 *  comment ("the orchestrator/operator calls this repeatedly ... until
 *  isLastBatch is true"). `batchSize` is optional (server default applies
 *  when omitted); this client never invents a value for it. */
export async function runAxisAffinityBackfillBatch(
  subjectType: AxisSweepSubjectType,
  batchSize?: number,
): Promise<AxisSweepBatchResult> {
  return apiFetch<AxisSweepBatchResult>('/admin/axis-affinities/backfill', {
    method: 'POST',
    body: JSON.stringify(batchSize ? { subjectType, batchSize } : { subjectType }),
  });
}

// =============================================================================
// MARKETING DRAFTS REVIEW (WO-3 Part C3, apps/api/routes/marketing-drafts.
// routes.ts). requireAuth+requireAdmin-gated server-side, same posture as the
// axis-sweep admin calls above — this client makes no admin decision of its
// own. Mirrors marketing_content_drafts (packages/schema) verbatim; "status"
// only ever moves draft -> approved | rejected via these two actions, never
// to any send/publish state (there is none — see the route file's header).
// =============================================================================

export type MarketingContentDraftKind = 'digest' | 'social' | 'blog' | 'email';
export type MarketingContentDraftStatus = 'draft' | 'approved' | 'rejected';

export interface MarketingDraftSourceRef {
  kind: string;
  description: string;
  value?: string | number;
  asOf?: string;
}

export interface MarketingContentDraft {
  id: string;
  kind: MarketingContentDraftKind;
  title: string;
  body: string;
  sourceRefs: MarketingDraftSourceRef[] | null;
  status: MarketingContentDraftStatus;
  generatedBy: string | null;
  aiCallLogId: string | null;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

/** GET /api/admin/marketing-drafts?status=draft — defaults to the review
 *  inbox (status='draft'); pass 'all' for every row regardless of status. */
export async function getMarketingDrafts(
  status: MarketingContentDraftStatus | 'all' = 'draft',
): Promise<MarketingContentDraft[]> {
  const result = await apiFetch<{ ok: true; drafts: MarketingContentDraft[] }>(
    `/admin/marketing-drafts?status=${encodeURIComponent(status)}`,
  );
  return result.drafts;
}

/** POST /api/admin/marketing-drafts/:id/approve — status='approved' only.
 *  Never sends or publishes anything (see route file header). */
export async function approveMarketingDraft(id: string): Promise<MarketingContentDraft> {
  const result = await apiFetch<{ ok: true; draft: MarketingContentDraft }>(
    `/admin/marketing-drafts/${encodeURIComponent(id)}/approve`,
    { method: 'POST' },
  );
  return result.draft;
}

/** POST /api/admin/marketing-drafts/:id/reject — status='rejected' only.
 *  Never deletes the row (no deletions, ever). */
export async function rejectMarketingDraft(id: string): Promise<MarketingContentDraft> {
  const result = await apiFetch<{ ok: true; draft: MarketingContentDraft }>(
    `/admin/marketing-drafts/${encodeURIComponent(id)}/reject`,
    { method: 'POST' },
  );
  return result.draft;
}

// =============================================================================
// E7 FRONT DOOR (LANE E7-PORT-THROUGHLINE, 2026-07-17) — the 7-card guided
// first-login flow. docs/diagrams/c4-e7-front-door-2026-07-17.html. Every
// shape below is copied WIRE-IDENTICAL from apps/web/src/lib/api.ts (L1901-
// 1974) — the SAME already-deployed apps/api/routes/profile.routes.ts backend
// serves both frontends; this app makes zero backend changes (the API +
// schema landed and deployed in R371, ahead of this port). GET/PUT extend the
// existing /profile route (never a parallel profile endpoint);
// completeOnboarding is the one genuinely new action (a stamp, not a field).
// =============================================================================

export type RelocationPosture = 'stay_local' | 'domestic' | 'international';

/** The subset of the server's resolved profile the onboarding flow reads.
 *  Named `declaredHomeCountry` (not `homeCountry`) to match the server's own
 *  disambiguation from the EXISTING geo-token-derived `homeCountry` field —
 *  see apps/api/lib/userProfile.ts's ResolvedUserProfile doc comment. */
export interface OnboardingProfile {
  userId: string;
  preferredName: string | null;
  declaredHomeCountry: string | null;
  postalCode: string | null;
  radiusMiles: number | null;
  onboardedAt: string | null;
  workRights: string[];
  targetMarkets: string[];
  targetGeoIds: string[];
  homeMetro: string | null;
  attributes: {
    phone?: string | null;
    /** SUPERSEDED-BUT-KEPT (E7-WALK-WAVE W1, 2026-07-17) — the original
     *  single-choice posture. A row saved before W1 carries only this key;
     *  never written by this app's save path anymore, but never dropped
     *  from the shape either (additive evolution). See lib/frontDoorCards.
     *  ts's resolveRelocationPostures for the read-side back-compat. */
    relocationPosture?: RelocationPosture | null;
    /** E7-WALK-WAVE W1 (2026-07-17) — the multi-select replacement: every
     *  posture the user toggled on, independently (local AND domestic AND
     *  international can all be true at once). The source of truth this
     *  app's save path writes going forward. */
    relocationPostures?: RelocationPosture[] | null;
    /** E7-WALK-WAVE W2 (2026-07-17) — the explicit "No others — home
     *  country only" declaration (profile.routes.ts's CRITICAL GUARD:
     *  deliberately NOT `workRights: []`, which would permanently disable
     *  the citizenship-derived default). true = declared; null/undefined =
     *  not declared. */
    workRightsNone?: boolean | null;
    [key: string]: unknown;
  };
}

export interface OnboardingState {
  profile: OnboardingProfile;
  /** Read-only auth identity (R367) — shown, never editable from this flow. */
  email: string | null;
  /** LIVE, never-stored suggestion from this request's own Cloudflare geo
   *  hint. Recomputed on every GET; null in local dev (no cf object) or when
   *  the signal is absent/malformed — never a guessed country. */
  suggestedHomeCountry: string | null;
  /** Server-computed gate — render verbatim, never re-derive client-side. */
  eligibleForOnboarding: boolean;
}

export async function getOnboardingState(): Promise<OnboardingState> {
  return apiFetch<OnboardingState>('/profile/onboarding');
}

/** One card's save — PUT /api/profile (the SAME editable-wildcard route
 *  every other profile write already uses). Any field PRESENT is written +
 *  marked a user override; omitted fields are left untouched; null
 *  explicitly clears. Each card sends exactly the field(s) IT owns. */
export interface OnboardingCardPatch {
  preferredName?: string | null;
  homeCountry?: string | null;
  postalCode?: string | null;
  radiusMiles?: number | null;
  workRights?: string[];
  targetMarkets?: string[];
  targetGeoIds?: string[];
  relocationPosture?: RelocationPosture | null;
  phone?: string | null;
  /** E7-WALK-WAVE W1 (2026-07-17) — the multi-select array. See
   *  OnboardingProfile.attributes's own doc comment for the additive
   *  relationship to relocationPosture above (never sent by this app
   *  anymore, but never removed from the wire contract). */
  relocationPostures?: RelocationPosture[];
  /** E7-WALK-WAVE W2 (2026-07-17) — the declared-none flag. true declares
   *  it; null clears a prior declaration (sent alongside a real `workRights`
   *  patch — see WorkRightsCard's own mutual-exclusion doc comment).
   *  NEVER paired with `workRights: []` — see profile.routes.ts's
   *  CRITICAL GUARD. */
  workRightsNone?: boolean | null;
}

export async function saveOnboardingCard(
  patch: OnboardingCardPatch,
): Promise<{ profile: OnboardingProfile }> {
  return apiFetch('/profile', { method: 'PUT', body: JSON.stringify(patch) });
}

/** The completion stamp — fires on finishing all 7 cards OR an explicit
 *  skip-all. NEVER called from the preview path (?onboarding=preview) — see
 *  lib/frontDoorCards.ts's isOnboardingPreview doc comment. */
export async function completeOnboarding(): Promise<{ onboardedAt: string }> {
  return apiFetch('/profile/onboarding/complete', { method: 'POST' });
}

// =============================================================================
// SEARCH SCENARIOS (L-K — the registered R386 follow-up). Three thin wrappers
// over routes that ALREADY exist (apps/api/routes/scenarios.routes.ts): the
// enriched list read, and the user-driven retire/reactivate pair. No backend
// change is made or needed by this lane — R386 shipped all three; the front end
// simply never called them.
//
//   GET  /api/scenarios                  → scenarios.routes.ts:53
//   POST /api/scenarios/:id/retire       → scenarios.routes.ts:187
//   POST /api/scenarios/:id/reactivate   → scenarios.routes.ts:208
//
// Retire/reactivate are BOTH non-destructive: retire stamps retired_at (a
// nullable stamp, never a DELETE), reactivate clears it. Nothing about a
// scenario — including its job_scores rows — is removed by either.
// =============================================================================

/** One row of GET /api/scenarios: the whole search_scenarios row (the route
 *  spreads `...s`) plus the three DERIVED staleness fields R386 added. Only the
 *  fields this app actually renders are typed here; the wire carries more
 *  (sortOrder, targetCompanies, rubricContext, timestamps) and adding a field
 *  is additive whenever a surface needs it. */
export interface ScenarioRecord {
  id: string;
  name: string;
  /** The user-facing PAUSE flag — deliberately NOT the same thing as retired
   *  (see retiredStale below). A paused scenario is skipped by
   *  loadActiveScenarios' `active = true` WHERE. */
  active: boolean;
  isPrimary: boolean;
  /** Location tokens, served verbatim (the derived label view over
   *  location_geo_ids). */
  locations: string[];
  /** The raw work_authorization column (varchar) — deliberately `string`, not
   *  a narrowed union: the FE maps KNOWN codes to plain words and omits
   *  anything it does not recognise (scenarioTruth.describeWorkAuthorization)
   *  rather than trusting a union that a future backend value would silently
   *  violate. */
  workAuthorization: string;
  remoteOk: boolean;
  /** The retire STAMP (ISO string over the wire) — non-null exactly when a
   *  user-driven retire is in force, and exactly what POST /reactivate clears.
   *  Null for a scenario that is retired-stale by DERIVATION only. */
  retiredAt: string | null;
  /** The optional free-text note captured at retire time. */
  retiredReason: string | null;
  /** DERIVED (R386) — the server's own verdict: is this scenario being held
   *  back from scoring/fit copy right now? TRUE for both a user retire and a
   *  profile-contradicting stale scenario. The SAME verdict the serve paths
   *  enforce, so the management screen can never disagree with what is
   *  actually being scored. */
  retiredStale: boolean;
  /** Why, as a code ('user_retired' | 'contradicts_home_no_declared_intent'
   *  today). Typed `string | null` on purpose — see scenarioTruth.ts's
   *  KnownStaleReason for why an unrecognised code must stay reachable rather
   *  than being coerced into a known one. Never rendered raw. */
  staleReason: string | null;
  /** The server's own honest detail sentence for that verdict. Rendered
   *  VERBATIM; the FE never composes one (it does not know the countries
   *  involved). Null when the scenario is servable. */
  staleDetail: string | null;
}

export interface ScenariosResult {
  scenarios: ScenarioRecord[];
}

/** GET /api/scenarios — every scenario the caller owns, each already enriched
 *  with the derived staleness verdict. Ordered by the server (sortOrder, then
 *  name); this client never re-sorts. */
export async function getScenarios(): Promise<ScenariosResult> {
  return apiFetch<ScenariosResult>('/scenarios');
}

/** POST /api/scenarios/:id/retire — stamps retired_at (clock-stamped
 *  server-side, ownership-scoped). `reason` is the user's own optional note;
 *  omitted entirely when absent, so the server records null rather than an
 *  invented explanation. Returns the updated row. */
export async function retireScenario(
  id: string,
  reason?: string,
): Promise<{ scenario: ScenarioRecord }> {
  return apiFetch<{ scenario: ScenarioRecord }>(`/scenarios/${id}/retire`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

/** POST /api/scenarios/:id/reactivate — clears the retire stamp. NOTE: the
 *  returned row is the raw DB row (this route does not re-run the staleness
 *  derivation), so callers re-read GET /scenarios afterwards rather than
 *  trusting this response's absent retiredStale — see useScenarios.ts. */
export async function reactivateScenario(id: string): Promise<{ scenario: ScenarioRecord }> {
  return apiFetch<{ scenario: ScenarioRecord }>(`/scenarios/${id}/reactivate`, { method: 'POST' });
}
